/**
 * Backend interface types for TypeGraph storage.
 *
 * The backend abstracts database operations, allowing different
 * SQL implementations (SQLite, PostgreSQL) behind a common interface.
 */
import {
  type Cardinality,
  type IndexEntity,
  type JsonScalar,
  type JsonValue,
  type KindEntity,
  type TemporalMode,
} from "../core/types";
import { type SqlTableNames } from "../query/compiler/schema";
import { type FulltextStrategy } from "../query/dialect/fulltext-strategy";
import {
  type VectorSlot,
  type VectorStrategy,
} from "../query/dialect/vector-strategy";
import { type SqlFragment } from "../query/sql-fragment";
import {
  type CompiledRowsSql,
  type CompiledSelectSql,
  type CompiledStatementSql,
  type CompiledTemporaryStatementSql,
} from "../query/sql-intent";
import { type SerializedSchema } from "../schema/types";
import { typeGraphGlobalSymbol } from "../utils/global-symbol";

// ============================================================
// Vector Search Types
// ============================================================

/**
 * Supported vector similarity metrics.
 */
export type VectorMetric = "cosine" | "l2" | "inner_product";

/**
 * Supported vector index types.
 */
export type VectorIndexType = "hnsw" | "ivfflat" | "none";

/**
 * The mechanism a strategy uses to combine a row filter with an *approximate*
 * (ANN) vector search.
 *
 * Every approximate search TypeGraph issues carries at least one filter: the
 * liveness predicate that excludes soft-deleted and out-of-validity rows. Add
 * a `.where(...)` predicate and it narrows further. Engines differ in where
 * they apply it relative to the index traversal:
 *
 * - `"filter-pushdown"` — the filter constrains the ANN candidate set itself.
 *   sqlite-vec's `vec0` KNN accepts primary-key `IN (SELECT …)` pushdown.
 * - `"iterative-scan"` — the engine re-enters the index, gathering more
 *   candidates until `LIMIT` rows survive the filter *or* it hits its own scan
 *   bound. pgvector >= 0.8 (`hnsw.iterative_scan` / `ivfflat.iterative_scan`,
 *   applied automatically by the backend).
 * - `"post-filter"` — the engine retrieves a fixed multiple of the page from
 *   the ANN index and applies the filter afterwards. libSQL's DiskANN
 *   `vector_top_k` is a table function with no filter pushdown, so this is the
 *   only shape available to it.
 *
 * This names what the strategy *asks the engine to do*. Whether the engine can
 * honor it — and whether honoring it is enough to fill a page — is
 * {@link FilteredApproximateSearch.guaranteesFullPage}.
 */
export type FilteredApproximateSearchMode =
  "filter-pushdown" | "iterative-scan" | "post-filter";

/**
 * How a filtered approximate (ANN) vector search behaves, and whether it can
 * silently return fewer rows than `limit` while more matches exist.
 *
 * Read `guaranteesFullPage` — not `mode` — to decide whether a short page is
 * possible. Only `"filter-pushdown"` guarantees a full page:
 *
 * - **sqlite-vec** pushes the filter into the KNN. Exact.
 * - **pgvector** re-enters the index, but the iterative scan stops at
 *   `hnsw.max_scan_tuples` / `ivfflat.max_probes`, and on **pgvector < 0.8**
 *   there is no iterative scan at all — the backend detects that at runtime,
 *   warns once, and the search stays `ef_search`-bounded, behaving like
 *   `"post-filter"`. Neither case is visible in a statically-declared `mode`,
 *   which is exactly why the guarantee is a separate field.
 * - **libSQL** over-fetches a fixed multiple of the page and filters
 *   afterwards, so it under-fills once more than that headroom is filtered out.
 *
 * A store with heavy tombstone drift — routine in a temporal store — is what
 * turns a short page from a theoretical caveat into an observed one. Exact
 * (`approximate: false`) searches are unaffected on every engine: they scan, so
 * the filter reaches every row.
 */
export type FilteredApproximateSearch = Readonly<{
  mode: FilteredApproximateSearchMode;
  /**
   * `true` only when a filtered approximate search is guaranteed to return
   * `limit` rows whenever `limit` matching rows exist — no engine-side scan
   * bound, no version dependence, no over-fetch headroom to exhaust.
   */
  guaranteesFullPage: boolean;
}>;

/**
 * Whether an engine exposes a PER-SEARCH knob for the ANN candidate frontier —
 * the parameter {@link VectorSearchParams.efSearch} maps onto.
 *
 * Declared, never inferred: `efSearch` is an accepted option, so a backend that
 * cannot apply it must refuse it rather than ignore it (AGENTS.md contract
 * discipline). This capability is what the shared refusal predicate reads, and
 * what a caller reads to know whether passing `efSearch` will be honored.
 *
 * - **pgvector**: `hnsw.ef_search`, applied per search with `SET LOCAL` inside
 *   the search's own transaction — hence `requiresTransactionScope`, since a
 *   session-wide `SET` would leak the override into concurrent searches.
 * - **sqlite-vec**: `tunable: false`. A vec0 KNN takes `k` and nothing else;
 *   there is no frontier to widen.
 * - **libSQL DiskANN**: `tunable: false`. `vector_top_k(idx, q, k)` is a table
 *   function with no per-query parameters; DiskANN's `search_l` is fixed at
 *   index-creation time.
 */
export type VectorSearchFrontierTuning =
  | Readonly<{
      tunable: true;
      /** Engine-native parameter `efSearch` maps to (`"hnsw.ef_search"`). */
      parameter: string;
      /**
       * The one index type whose search honors the parameter. `efSearch` on a
       * slot of any other index type is refused, not ignored: an IVFFlat or
       * brute-force slot would silently discard it.
       */
      indexType: VectorIndexType;
      /**
       * `true` when applying the parameter needs a transaction to scope it to
       * the one search. A backend without interactive transactions refuses
       * `efSearch` rather than leaking a session-wide setting.
       */
      requiresTransactionScope: boolean;
    }>
  | Readonly<{
      tunable: false;
      /**
       * Why this engine has no per-search frontier knob — surfaced in the
       * refusal's details so the state is named rather than implied.
       */
      reason: string;
    }>;

/**
 * Vector search capabilities.
 */
export type VectorCapabilities = Readonly<{
  /** Whether the backend supports vector operations */
  supported: boolean;
  /** Supported similarity metrics */
  metrics: readonly VectorMetric[];
  /** Supported index types */
  indexTypes: readonly VectorIndexType[];
  /** Maximum dimensions supported */
  maxDimensions: number;
  /**
   * How a filtered approximate search bounds recall — and whether it can
   * silently return a short page. See {@link FilteredApproximateSearch}.
   *
   * Required, so a new vector strategy cannot omit the declaration and inherit
   * an engine promise it does not keep.
   */
  filteredApproximateSearch: FilteredApproximateSearch;
  /**
   * Whether a per-search ANN frontier override (`efSearch`) can be applied.
   * See {@link VectorSearchFrontierTuning}.
   *
   * Required, so a new vector strategy must state whether it honors the
   * option instead of inheriting silence — the exact defect this closes.
   */
  searchFrontierTuning: VectorSearchFrontierTuning;
}>;

// ============================================================
// Fulltext Search Types
// ============================================================

/**
 * Query modes for fulltext search.
 *
 * - "websearch": Google-style syntax (quoted phrases, +required, -excluded).
 *    Postgres: `websearch_to_tsquery`. SQLite: translated to FTS5 MATCH.
 * - "phrase": treats the whole query as a phrase.
 *    Postgres: `phraseto_tsquery`. SQLite: FTS5 `"..."` phrase.
 * - "plain": splits on whitespace and ANDs terms.
 *    Postgres: `plainto_tsquery`. SQLite: default FTS5 AND.
 * - "raw": dialect-native syntax passed through unchanged.
 */
export type FulltextQueryMode = "websearch" | "phrase" | "plain" | "raw";

/**
 * Fulltext search capabilities declared by a backend.
 */
export type FulltextCapabilities = Readonly<{
  /** Whether the backend supports fulltext operations */
  supported: boolean;
  /**
   * Language / tokenizer names understood by this backend.
   * Postgres: installed regconfigs (english, simple, ...).
   * SQLite FTS5: tokenizer names (unicode61, porter, trigram).
   */
  languages: readonly string[];
  /** Whether phrase queries are supported. */
  phraseQueries: boolean;
  /** Whether prefix (`foo*`) queries are supported. */
  prefixQueries: boolean;
  /** Whether highlighting / snippets are supported. */
  highlighting: boolean;
}>;

/**
 * Whole-graph analytics capabilities declared by a backend.
 *
 * `supported` means the backend can pin one transactional connection while
 * an algorithm creates, updates, and drops session-local temporary state.
 * `mathFunctions` records whether transcendental SQL functions needed by
 * deferred algorithms such as MCL/spectral are available; exact WCC does not
 * require them.
 */
export type GraphAnalyticsCapabilities = Readonly<{
  supported: boolean;
  mathFunctions: boolean;
}>;

/**
 * How much of the contribution health lifecycle a backend can serve.
 *
 * The three rungs escalate — probe (read-only) → repair
 * (non-destructive) → rebuild (destructive) — and each is declared
 * separately because a backend can genuinely stop at any of them. A
 * backend that provisions contributions but cannot probe its own catalog
 * is a real gap, and the honest answer to `store.probeContributions()`
 * there is a refusal, not `ready`: "assessed and healthy" and "never
 * looked" must never share a return value.
 */
export type ContributionCapabilities = Readonly<{
  /**
   * Whether strategy-owned contribution tables are provisioned and
   * attested by durable markers on this backend at all. `false` means
   * there is nothing to assess, and the probe reports no entries rather
   * than refusing.
   */
  supported: boolean;
  /**
   * Whether the backend can cross its durable markers against the live
   * catalog — `store.probeContributions()` and
   * `store.verifyContributions()`. False with `supported: true` is the
   * declared gap: both refuse with `ConfigurationError`.
   */
  probe: boolean;
  /**
   * Whether the backend can run the destructive drop → recreate →
   * repopulate → stamp rebuild (`store.rebuildContribution()`). Requires
   * both a strategy that declares `dropDdl` and a transactional schema
   * fence to run it under; false means `rebuildContribution` refuses.
   *
   * Describes the fulltext projection only. `rebuildContribution("vector")`
   * refuses on every backend regardless of this flag — embeddings exist
   * only in the storage a rebuild would drop, so there is nothing to
   * reconstruct them from — and `reembedVectorField` is the sanctioned
   * destructive path there.
   */
  rebuild: boolean;
}>;

// ============================================================
// SQL Dialect & Capabilities
// ============================================================

import { type SqlDialect } from "../query/dialect/types";

export type { SqlDialect } from "../query/dialect/types";

import { type RecursiveTraversalCapability } from "./capabilities/recursive-traversal";

export type { RecursiveTraversalCapability } from "./capabilities/recursive-traversal";

import {
  type FenceSql,
  type WriteFenceDeclaration,
} from "./capabilities/write-fence";

export type {
  FenceSql,
  WriteFenceDeclaration,
} from "./capabilities/write-fence";

import { type BackendCatalogProbes } from "./capabilities/catalog";
import { type LineageMembers } from "./capabilities/lineage";
import { type EngineRecordedTimeMembers } from "./capabilities/recorded-time";

export type {
  BackendCatalogProbes,
  CatalogColumn,
  CatalogIndexBehavior,
  IndexState,
  NormalizedColumnKind,
  TableState,
} from "./capabilities/catalog";
export type {
  EngineRevision,
  EntityKey,
  LineageDelta,
  LineageMembers,
  LineageSession,
} from "./capabilities/lineage";
export type {
  EngineRecordedRevision,
  EngineRecordedTimeMembers,
  RecordedSourceTable,
  RecordedTimeSession,
} from "./capabilities/recorded-time";

/**
 * Backend capabilities that vary by dialect.
 */
export type BackendCapabilities = Readonly<{
  /** How this backend executes work and establishes atomicity. */
  execution: Readonly<{
    /** Whether the backend can hold an interactive callback/session transaction. */
    interactiveTransactions: boolean;
    /**
     * Where this exact backend object can execute a conformance-earned atomic
     * batch. `root` owns the transaction boundary; `session` is already bound
     * to the transaction that makes a closed statement sequence atomic.
     */
    atomicBatch: "none" | "root" | "session";
    /**
     * How this backend groups a multi-statement write into one unit: an
     * `"interactive"` callback/session transaction that can hold a fenced
     * conversation across several round trips; `"optimistic-retry"`, the
     * same interactive transaction on an engine whose write fence resolves
     * `mechanism: "row"` with `conflict: "commit-time"` — two acquirers of
     * one fence row both proceed and the loser's COMMIT fails, so every
     * store-owned unit of work must be prepared to replay from the top; a
     * `"batch"` atomic program with no such transaction; or `"none"` when it
     * offers neither and a managed write can only run its statements one at
     * a time with no atomicity across them.
     *
     * Derived, never hand-declared, on every bundled backend through
     * `deriveUnitOfWork` (`backend/capabilities/execution.ts`) —
     * `"optimistic-retry"` when `interactiveTransactions` is true AND the
     * caller supplied the resolved write-fence plan's `conflict` fact as
     * `"commit-time"`, else `"interactive"` when `interactiveTransactions`
     * is true, else `"batch"` when `atomicBatch` is not `"none"`, else
     * `"none"` — overwriting whatever a profile's own `declaredCapabilities`
     * set. `finalizeEngineCapabilities` is the one place that resolves this
     * fact fresh, from the root profile's own `row`-mechanism plan; every
     * later derivation boundary (`downgradeAtomicBatch`,
     * `scopeAtomicBatchToSession`) carries it forward instead of
     * re-resolving it, by reading whether its own source object was already
     * `"optimistic-retry"` — so the tier survives a derived or
     * session-scoped backend for as long as `interactiveTransactions` stays
     * `true`. Optional so a custom `GraphBackend` implementation, which
     * nothing derives this for, is not forced to declare it.
     *
     * `src/store/operations/write-transaction.ts`'s retry-routing helpers
     * are the store-owned `"optimistic-retry"` consumers: every
     * `runWritePlan`/`runHookedWritePlan`/
     * `runAutocommitSingleStatementWritePlan` call, `runIdentityMutation`,
     * `rebuildIdentityClosureWithSchemaFence`, `rebuildContribution`, and the
     * index-materialization claim/record calls each wrap their write in
     * `runRetriedUnit` when this reads `"optimistic-retry"` (and, for the
     * transaction-opening ones, the write opens its own transaction rather
     * than joining an existing one). Two backend-owned transactions consult
     * the SAME tier directly, outside the store's write path entirely:
     * PostgreSQL's graph-template instantiation row branch and
     * `runSchemaWriteTransaction` (behind `commitSchemaVersion` and its
     * three siblings), both in `src/backend/drizzle/postgres.ts` — each
     * acquires the schema-commit fence row in a `db.transaction(...)` it
     * opens directly, so each wraps that whole transaction in
     * `runRetriedUnit` itself rather than routing through the store's
     * helpers. Two further readers key off the
     * `"batch"` value: the batch-tier write verdict
     * (`resolveBatchWriteVerdict` in
     * `backend/capabilities/batch-write-verdict.ts`) and the autocommit
     * single-statement eligibility gate (`canFuseSchemaFenceInFirstWrite`).
     * Absent is treated as anything but `"batch"`: `resolveBatchWriteVerdict`
     * answers `program` (its "not a batch-tier limitation" verdict) for it,
     * so a backend that declares neither an interactive transaction nor an
     * atomic batch still fails closed on a write that needs one — through
     * that write's own capability check, not through a batch-tier refusal it
     * never earned.
     */
    unitOfWork?: "interactive" | "optimistic-retry" | "batch" | "none";
  }>;
  /** Whether the backend supports SQL window functions such as ROW_NUMBER() */
  windowFunctions: boolean;
  /**
   * Whether aggregate calls may contain their own `ORDER BY` clause, as used
   * by ordered scalar and record collection. Absent is `false`: custom and remote
   * backends must opt in only when their active engine accepts that syntax.
   */
  orderedAggregates?: boolean;
  /**
   * Whether `updateNode` / `updateEdge` honor `clearValidTo: true` by storing
   * SQL NULL in `valid_to`. Absent is `false`: custom backends must opt in so
   * the store refuses every explicit clear-bearing call before lookup,
   * coalescing, or write instead of making support depend on row state.
   */
  clearValidTo?: boolean;
  /**
   * Whether the backend's `execute()` supports `UPDATE … RETURNING`. Absent or
   * `true` means supported (every engine TypeGraph ships — SQLite ≥ 3.35,
   * PostgreSQL ≥ 8.2 — supports it). A custom backend whose engine cannot run
   * `RETURNING` must set this to `false` so recorded-time history capture
   * (`history: true`), which relies on `UPDATE … RETURNING` on its hot path,
   * is refused up front instead of failing mid-flush.
   */
  returning?: boolean;
  /**
   * Maximum number of bound parameters the engine accepts in one statement.
   * SQLite defaults to 999 (raisable at compile time via
   * `SQLITE_MAX_VARIABLE_NUMBER`), while hosted SQLite runtimes may impose a
   * lower platform ceiling. PostgreSQL's wire protocol encodes a 65535-count,
   * but postgres.js accepts at most 65533 bound values, so the bundled
   * PostgreSQL capability advertises that lower shared-driver ceiling.
   * Recorded-time capture and recorded point reads size their multi-row
   * statements to this ceiling — the same budget the backend's own batched
   * inserts use — instead of a conservative dialect-blind constant. Custom
   * runtimes can override a detected or probed limit here, but hosted platform
   * hard ceilings may only be lowered. Absent means the recorded-time fallback
   * budget applies.
   */
  maxBindParameters?: number;
  /**
   * Whether this backend supplies the claim relations that fence declared
   * constraints (`uniques`, re-keyed onto claim axes, plus
   * `typegraph_edge_claims`) AND implements the claim members.
   *
   * Absent means `false`: the backend predates the claim relations, keeps every
   * fence it has today, and is listed as such in the parity matrix. Never
   * INFERRED from member presence — a projection that forwarded `capabilities`
   * while dropping a method would otherwise yield a verdict read from a
   * different object than the write goes to. `claimSupport`
   * (`store/claims/backing.ts`) is the one reader, and it refuses a backend
   * whose declaration and surface disagree in either direction.
   */
  readonly constraintClaims?: boolean;
  /**
   * Whether the command port can atomically apply the claim portion of
   * a node create. Absent means projection-only: method presence alone is not
   * evidence that a custom backend understands the newer plan field, so the
   * store retains the standalone claim fallback.
   */
  readonly atomicNodeInsertClaims?: boolean;
  /** Whether edge writes atomically persist and arbitrate `matchIdentity`. */
  readonly durableEdgeMatchIdentity?: boolean;
  /** Vector search capabilities (undefined if not configured) */
  vector?: VectorCapabilities | undefined;
  /** Fulltext search capabilities (undefined if not configured) */
  fulltext?: FulltextCapabilities | undefined;
  /** Whole-graph analytics capabilities (undefined when unavailable). */
  graphAnalytics?: GraphAnalyticsCapabilities | undefined;
  /**
   * How far up the contribution health ladder this backend goes
   * (undefined on a backend with no contribution machinery at all,
   * equivalent to every member `false`).
   */
  contributions?: ContributionCapabilities | undefined;
  /**
   * Whether this engine can compute a bounded transitive closure of a
   * relation in one round trip (a recursive CTE, or a graph-native
   * expansion operator).
   *
   * Absent means SUPPORTED. Every engine TypeGraph ships supports it, and
   * every custom backend on this release already has every recursion site
   * run against it unconditionally: making absence mean `false` would
   * refuse traversals that work today. See
   * {@link RecursiveTraversalCapability} for the full contract.
   */
  recursiveTraversal?: RecursiveTraversalCapability | undefined;
  /**
   * How this backend excludes concurrent writers: a keyed advisory lock, a
   * single writer slot the engine serializes by construction, or a
   * deployment-level promise that this process serializes its own writes and
   * no other client writes to the database — see
   * {@link WriteFenceDeclaration} for the full `mechanism`/`drain` contract.
   *
   * Absent means `unfenced` for any backend the first-party factories did
   * not build (`resolveWriteFencePlan`, `src/backend/capabilities/write-fence.ts`):
   * an undeclared custom backend is refused at construction for Operational
   * Identity and for TypeGraph-owned recorded-clock allocation (`history` /
   * `revisionTracking`) rather than silently emitting locks the engine may
   * not honor.
   */
  writeFence?: WriteFenceDeclaration | undefined;
}>;

export type BackendExecutionCapabilities = BackendCapabilities["execution"];

/**
 * Capability overrides accepted by bundled backend factories.
 *
 * Root atomic-batch support is deliberately absent: bundled factories derive
 * it from the transport they actually discover and register. Callers may
 * override interactive transaction availability for an otherwise unknown
 * driver, but cannot advertise an executor the factory did not find.
 */
export type BundledBackendCapabilityOverrides = Readonly<
  Omit<Partial<BackendCapabilities>, "execution"> & {
    execution?: Readonly<{
      interactiveTransactions?: boolean;
    }>;
  }
>;

/** Keeps session-scoped analytics honest when a required SQL feature is absent. */
export function normalizeGraphAnalyticsCapabilities(
  capabilities: BackendCapabilities,
): BackendCapabilities {
  if (
    capabilities.graphAnalytics?.supported !== true ||
    (capabilities.execution.interactiveTransactions &&
      capabilities.windowFunctions &&
      capabilities.returning !== false)
  ) {
    return capabilities;
  }
  return {
    ...capabilities,
    graphAnalytics: { ...capabilities.graphAnalytics, supported: false },
  };
}

/**
 * Returns whether the supplied backend or capability declaration can keep an
 * interactive transaction open across awaited statements.
 */
export function supportsInteractiveTransactions(
  backendOrCapabilities:
    BackendCapabilities | Readonly<{ capabilities: BackendCapabilities }>,
): boolean {
  return "capabilities" in backendOrCapabilities ?
      backendOrCapabilities.capabilities.execution.interactiveTransactions
    : backendOrCapabilities.execution.interactiveTransactions;
}

/** Returns whether this exact root declares certified atomic batch support. */
export function supportsRootAtomicBatch(
  backendOrCapabilities:
    BackendCapabilities | Readonly<{ capabilities: BackendCapabilities }>,
): boolean {
  const capabilities =
    "capabilities" in backendOrCapabilities ?
      backendOrCapabilities.capabilities
    : backendOrCapabilities;
  return capabilities.execution.atomicBatch === "root";
}

/** Returns whether this exact object owns any certified atomic batch session. */
export function supportsAtomicBatch(
  backendOrCapabilities:
    BackendCapabilities | Readonly<{ capabilities: BackendCapabilities }>,
): boolean {
  const capabilities =
    "capabilities" in backendOrCapabilities ?
      backendOrCapabilities.capabilities
    : backendOrCapabilities;
  return capabilities.execution.atomicBatch !== "none";
}

// ============================================================
// Row Types (Database Records)
// ============================================================

/**
 * A node/edge row's `props` column as the driver returned it: SQLite always
 * yields the JSON text; PostgreSQL drivers yield the jsonb value already
 * parsed. Keeping the driver's shape avoids a per-row
 * parse→stringify→re-parse round trip on the PostgreSQL read path —
 * consumers normalize through {@link rowPropsToObject} /
 * {@link rowPropsToJsonText} at the point of use.
 */
export type RowProps = string | Readonly<Record<string, unknown>>;

/** The row's props as an object, parsing only when the driver gave text. */
export function rowPropsToObject(props: RowProps): Record<string, unknown> {
  return typeof props === "string" ?
      (JSON.parse(props) as Record<string, unknown>)
    : props;
}

/** The row's props as JSON text, serializing only when the driver gave an object. */
export function rowPropsToJsonText(props: RowProps): string {
  return typeof props === "string" ? props : JSON.stringify(props);
}

/**
 * A row from the typegraph_nodes table.
 */
export type NodeRow = Readonly<{
  graph_id: string;
  kind: string;
  id: string;
  props: RowProps;
  version: number;
  valid_from: string | undefined;
  valid_to: string | undefined;
  created_at: string;
  updated_at: string;
  deleted_at: string | undefined;
}>;

/**
 * A {@link NodeRow} proven live (not soft-deleted). Functions that must only
 * ever touch live rows — the live-row update pipeline, soft delete — take
 * this type so a caller holding a possibly-tombstoned row is forced through
 * {@link isLiveNodeRow} first, instead of silently running live-row side
 * effects (uniqueness reservations, embedding/fulltext sync) for a row that
 * stays invisible.
 */
export type LiveNodeRow = NodeRow & Readonly<{ deleted_at: undefined }>;

/**
 * A {@link NodeRow} proven soft-deleted. A resurrect targets exactly this
 * shape; see {@link isTombstonedNodeRow}.
 */
export type TombstonedNodeRow = NodeRow & Readonly<{ deleted_at: string }>;

export function isLiveNodeRow(row: NodeRow): row is LiveNodeRow {
  return row.deleted_at === undefined;
}

export function isTombstonedNodeRow(row: NodeRow): row is TombstonedNodeRow {
  return row.deleted_at !== undefined;
}

/**
 * The read-only slice of a backend. Give this type to code that is
 * semantically a read — probes, gates, support-graph loads — so a read path
 * structurally CANNOT open a write transaction, mutate rows, or execute raw
 * SQL: those members do not exist on the type. Extend the pick as read paths
 * need more of the read surface.
 */
export type GraphReadBackend = Pick<
  GraphBackend,
  | "dialect"
  | "getNode"
  | "getEdge"
  | "findNodesByKind"
  | "findEdgesByKind"
  | "findEdgesByHeterogeneousEndpointSet"
  | "findEdgesConnectedTo"
>;

/**
 * A row from the typegraph_edges table.
 */
export type EdgeRow = Readonly<{
  graph_id: string;
  id: string;
  kind: string;
  from_kind: string;
  from_id: string;
  to_kind: string;
  to_id: string;
  props: RowProps;
  match_identity_name?: string;
  match_identity_key?: string;
  valid_from: string | undefined;
  valid_to: string | undefined;
  created_at: string;
  updated_at: string;
  deleted_at: string | undefined;
}>;

/** The schema-declared identity persisted with an edge row. */
export type EdgeMatchIdentityStorage = Readonly<{
  name: string;
  key: string;
}>;

/**
 * A row from the typegraph_node_uniques table.
 */
export type UniqueRow = Readonly<{
  graph_id: string;
  node_kind: string;
  constraint_name: string;
  key: string;
  node_id: string;
  concrete_kind: string;
  deleted_at: string | undefined;
}>;

/**
 * A row from the typegraph_schema_versions table.
 */
export type SchemaVersionRow = Readonly<{
  graph_id: string;
  version: number;
  schema_hash: string;
  schema_doc: string; // JSON string
  created_at: string;
  is_active: boolean;
}>;

/** A durable, server-resident materialized schema template. */
export type GraphTemplateRow = Readonly<{
  template_id: string;
  schema_hash: string;
  schema_doc: string;
  created_at: string;
}>;

// ============================================================
// Insert Parameters
// ============================================================

/**
 * Parameters for inserting a node.
 */
export type InsertNodeParams = Readonly<{
  graphId: string;
  kind: string;
  id: string;
  props: Readonly<Record<string, unknown>>;
  /**
   * Omitted (`undefined`): the insert stamps its own creation timestamp —
   * UNLESS a stated `validTo` at or before that instant would make the stored
   * window readable at no coordinate, in which case the row is stored with no
   * lower bound ("ended at T, start unknown"). See
   * `resolveStampedValidityLowerBound`, which every insert builder decides
   * through. Custom backend implementations can import that owner from
   * `@nicia-ai/typegraph/backend` rather than reimplementing the rule.
   * `null`: preserves an explicit open-left validity window (no lower
   * bound) — used by interchange import to round-trip a row that was
   * already NULL, instead of re-stamping it to the import's own timestamp.
   */
  validFrom?: string | null;
  validTo?: string;
}>;

/** One validated plain caller-ID member of the heterogeneous node upsert CTE. */
export type HeterogeneousNodeUpsertEntry = Readonly<{
  kind: string;
  id: string;
  /** Complete create-parsed document used for inserts and resurrections. */
  props: Readonly<Record<string, unknown>>;
  /** Caller patch used only when the target row is already live. */
  updateProps: Readonly<Record<string, unknown>>;
}>;

/** Exact-session input for the PostgreSQL heterogeneous node upsert lowering. */
export type HeterogeneousNodeUpsertParams = Readonly<{
  entries: readonly HeterogeneousNodeUpsertEntry[];
  schemaFence: SchemaWriteFenceParams;
}>;

/**
 * A backend validity-end mutation. Omission preserves the stored end,
 * `validTo` sets it, and `clearValidTo` reopens the window. The union keeps the
 * two write actions mutually exclusive without exposing SQL `NULL`.
 */
export type BackendValidityEndMutation =
  | Readonly<{ validTo?: string; clearValidTo?: never }>
  | Readonly<{ validTo?: never; clearValidTo: true }>;

/** Parameters for updating a node. */
export type UpdateNodeParams = Readonly<{
  graphId: string;
  kind: string;
  id: string;
  props: Readonly<Record<string, unknown>>;
  /**
   * Applied when resurrecting a tombstone, which RESETS the window. Omitted
   * means the resurrection instant — unless a stated `validTo` at or before it
   * would leave the row readable at no coordinate, in which case the
   * resurrection stores no lower bound. Same rule, same owner, as an insert:
   * `resolveStampedValidityLowerBound`.
   *
   * The store's own resurrection paths never omit it. Their window guard has to
   * judge the bound the write will STORE, so they resolve it through that owner
   * against the instant they sampled and pass the result — `null` included,
   * which is how "store no lower bound" is spelled here. Omission is for callers
   * with no such verdict to honor; it lets this builder decide against its own,
   * strictly later sample.
   */
  validFrom?: string | null;
  /**
   * The effective `valid_from` this write ASSERTS the target row already
   * carries, stated only by a caller whose decision DEPENDED on it.
   *
   * `(graph_id, kind, id)` does not pin a row across time. A caller that
   * validated the document's window against the bound it probed — interchange
   * import's `onConflict: "update"` is the case — decided what to write from a
   * value a concurrent `hardDelete` + recreate can replace between the probe
   * and the write, so a predicate on identity alone lets that decision land on
   * a row it was never computed for: the document's stated `validFrom` is
   * ignored, or a `valid_to` is persisted below the new row's `valid_from`.
   * Stating the bound here puts it in the UPDATE's own `WHERE`, which is the
   * only placement the race cannot slip past.
   *
   * NULL-SAFE: `null` asserts the row has NO lower bound (`IS NULL`), a string
   * asserts equality, and omitting it asserts nothing. `null` and `undefined`
   * are therefore NOT interchangeable here — see `expectedValidFromPredicate`.
   *
   * A backend MUST apply the predicate when it is present, on the same terms as
   * {@link UpdateEdgeParams.kind}: silently ignoring it re-opens the window the
   * caller stated it to close. A write whose stated bound does not match affects
   * zero rows and surfaces as a `no_row_returned` `DatabaseOperationError`.
   */
  expectedValidFrom?: string | null;
  incrementVersion?: boolean;
  /** If true, clears deleted_at (un-deletes the node). Used by upsert. */
  clearDeleted?: boolean;
}> &
  BackendValidityEndMutation;

/**
 * Parameters for updating a set of live nodes selected by a compiled
 * TypeGraph query.
 *
 * The candidate query must expose the named node-id column from the same graph
 * and kind. The backend independently fences the rows it writes to
 * `graphId` and `kind`; constructing the matching candidate query remains the
 * caller's responsibility.
 * Property values replace top-level keys, including preserving an explicit
 * JSON `null` value.
 *
 * This is a storage primitive: callers that expose it as a graph mutation are
 * responsible for schema validation and for synchronizing uniqueness,
 * fulltext, and vector sidecars. The result contains after-images only; callers
 * that need previous property values must freeze the candidate set and retain
 * its before-images in the same transaction before invoking this method.
 */
export type UpdateNodeSetParams = Readonly<{
  operation: "updateWhere";
  graphId: string;
  kind: string;
  patch: Readonly<Record<string, JsonValue>>;
  /** Top-level properties to remove (the storage form of an `undefined` patch value). */
  unsetProperties?: readonly string[];
  candidateIds: CompiledSelectSql;
  /** Projected SQL column holding the candidate node id (for example `n_id`). */
  candidateIdColumn: string;
}>;

/**
 * A guarded set update whose exact current-value predicates are applied by the
 * same outer UPDATE that writes the row. Kept as a distinct backend port so an
 * older/custom `updateNodeSet` implementation can never silently ignore the
 * compare-and-set fence.
 */
export type NodePropertyExpectation =
  Readonly<{ kind: "value"; value: JsonScalar }> | Readonly<{ kind: "absent" }>;

export type CompareAndSetNodeParams = Readonly<{
  operation: "compareAndSet";
  graphId: string;
  kind: string;
  patch: Readonly<Record<string, JsonValue>>;
  unsetProperties?: readonly string[];
  candidateIds: CompiledSelectSql;
  candidateIdColumn: string;
  /**
   * Exact scalar-or-absence predicates re-checked by the outer UPDATE. This
   * discriminated map cannot be assigned to an ordinary set update.
   */
  expected: Readonly<Record<string, NodePropertyExpectation>>;
}>;

/** The after-images changed by {@link GraphBackend.updateNodeSet}. */
export type UpdateNodeSetResult = Readonly<{
  affectedCount: number;
  rows: readonly NodeRow[];
}>;

/** One resolved, whole-row replacement in a guarded node batch. */
export type ResolvedNodeUpdateBatchEntry = Readonly<{
  graphId: string;
  kind: string;
  id: string;
  props: Readonly<Record<string, unknown>>;
  /** The preimage version the batch must still observe for every member. */
  expectedVersion: number;
}>;

/**
 * Replaces several distinct live rows in one statement and returns their
 * after-images. The all-or-nothing version gate makes this safe to use before
 * rebuilding shared sidecars in a portable transaction.
 */
export type ResolvedNodeUpdateBatchParams = Readonly<{
  entries: readonly ResolvedNodeUpdateBatchEntry[];
}>;

/**
 * Parameters for deleting a node (soft delete).
 */
export type DeleteNodeParams = Readonly<{
  graphId: string;
  kind: string;
  id: string;
}>;

/**
 * Parameters for inserting an edge.
 */
export type InsertEdgeParams = Readonly<{
  graphId: string;
  id: string;
  kind: string;
  fromKind: string;
  fromId: string;
  toKind: string;
  toId: string;
  props: Readonly<Record<string, unknown>>;
  /**
   * Durable schema-declared match identity. Both values are written with the
   * edge row and arbitrated by the database's edge-identity unique key.
   */
  matchIdentity?: EdgeMatchIdentityStorage;
  /**
   * Omitted (`undefined`): the insert stamps its own creation timestamp —
   * UNLESS a stated `validTo` at or before that instant would make the stored
   * window readable at no coordinate, in which case the row is stored with no
   * lower bound ("ended at T, start unknown"). See
   * `resolveStampedValidityLowerBound`, which every insert builder decides
   * through. Custom backend implementations can import that owner from
   * `@nicia-ai/typegraph/backend` rather than reimplementing the rule.
   * `null`: preserves an explicit open-left validity window (no lower
   * bound) — used by interchange import to round-trip a row that was
   * already NULL, instead of re-stamping it to the import's own timestamp.
   */
  validFrom?: string | null;
  validTo?: string;
}>;

/**
 * Write-only session exposed inside {@link GraphBackend.trustedImport}.
 *
 * The caller has accepted the trusted-import contract: properties, endpoint
 * references, uniqueness, and cardinality are not validated. Backends may
 * therefore use engine-native ingestion primitives that bypass the normal
 * store write pipeline.
 */
export type TrustedImportSession = Readonly<{
  insertNodes: (params: readonly InsertNodeParams[]) => Promise<void>;
  insertEdges: (params: readonly InsertEdgeParams[]) => Promise<void>;
}>;

/**
 * Parameters for updating an edge.
 */
export type UpdateEdgeParams = Readonly<{
  graphId: string;
  id: string;
  /**
   * The kind this write ASSERTS the target row already carries.
   *
   * Edge ids are graph-global while public collections are kind-scoped, so a
   * kind-scoped write has an expected kind and must not land on a row carrying
   * a different one. Stating it here puts the predicate in the write
   * statement's own `WHERE`, which is the only placement a concurrent
   * `hardDelete` + recreate cannot slip past: a read-then-write pair keyed on
   * `(graph_id, id)` alone re-resolves that id between the read and the write
   * under PostgreSQL READ COMMITTED. A write whose stated kind does not match
   * affects zero rows.
   *
   * A backend MUST apply the predicate when it is present — silently ignoring
   * it re-opens the window the caller stated it to close, and the ignoring
   * backend looks correct until it is raced.
   * `tests/edge-write-self-verification.test.ts` asserts the behavior against
   * the built-in backends.
   *
   * Omitted where the operation legitimately spans kinds: the node delete
   * cascade removes every connected edge whatever its kind, so it states none.
   * {@link DeleteEdgeParams} and {@link HardDeleteEdgeParams} carry the same
   * field with the same contract.
   */
  kind?: string;
  /**
   * The ENDPOINTS this write asserts the target row already carries, stated
   * only by a write that actually checked them.
   *
   * Kind alone does not pin a row's identity. An edge's endpoints are immutable
   * for a given row, but the ID is not: a concurrent `hardDelete` + recreate
   * under the SAME kind with DIFFERENT endpoints satisfies a kind-only
   * predicate, so an upsert that resolved this id BY endpoints could otherwise
   * write to an edge pointing somewhere it never looked. Carrying them here
   * closes that, on the same terms and in the same `WHERE` as `kind`.
   *
   * All four move together or not at all — they are one assertion. A plain
   * `update` on a kind-scoped collection states none of them, because it
   * resolved the edge by id and kind and made no claim about where it points;
   * predicating on endpoints it never checked would refuse legitimate writes.
   * The same MUST-apply contract as `kind` binds a backend that receives them.
   */
  fromKind?: string;
  fromId?: string;
  toKind?: string;
  toId?: string;
  props: Readonly<Record<string, unknown>>;
  /**
   * Applied when resurrecting a tombstone, where it asserts a COMPLETE window:
   * `validTo` is rewritten alongside it (omitted `validTo` reopens the window).
   * Omitting `validFrom` on a resurrection leaves the stored window in place.
   */
  validFrom?: string | null;
  /**
   * The effective `valid_from` this write asserts the target row already
   * carries. Same three states, same NULL-safety, and the same MUST-apply
   * contract as {@link UpdateNodeParams.expectedValidFrom}; stated by the same
   * kind of caller, one whose verdict READ that bound.
   *
   * Separate from the immutable-identity components above because it is not
   * immutable: a resurrection rewrites `valid_from`. It is asserted for the
   * opposite reason — precisely because it can change, a decision computed from
   * it must be fenced against it having changed.
   */
  expectedValidFrom?: string | null;
  /**
   * The `valid_to` state a constraint decision read. NULL-safe and MUST apply
   * when present, like `expectedValidFrom`; used to fence ended-to-open edges.
   */
  expectedValidTo?: string | null;
  clearDeleted?: boolean;
}> &
  BackendValidityEndMutation;

/**
 * Parameters for deleting an edge (soft delete).
 */
export type DeleteEdgeParams = Readonly<{
  graphId: string;
  id: string;
  /** See {@link UpdateEdgeParams.kind}. */
  kind?: string;
}>;

/**
 * Parameters for hard deleting a node (permanent removal).
 */
export type HardDeleteNodeParams = Readonly<{
  graphId: string;
  kind: string;
  id: string;
}>;

/**
 * Parameters for hard deleting an edge (permanent removal).
 */
export type HardDeleteEdgeParams = Readonly<{
  graphId: string;
  id: string;
  /** See {@link UpdateEdgeParams.kind}. */
  kind?: string;
}>;

/** Parameters for a batched edge delete (soft or hard). */
export type DeleteEdgesBatchParams = Readonly<{
  graphId: string;
  ids: readonly string[];
  /** See {@link UpdateEdgeParams.kind}. */
  kind?: string;
}>;

// ============================================================
// Embedding Parameters
// ============================================================

/**
 * Parameters for inserting or updating an embedding.
 *
 * `dimensions`, `metric`, and `indexType` together resolve the slot's
 * typed per-`(nodeKind, fieldPath)` storage (the strategy needs the fixed
 * dimension for the column type, and the metric/index type to address the
 * right ANN structure). The store populates them from the schema's
 * `embedding()` declaration via `getEmbeddingDimensions()` /
 * `getEmbeddingIndex()`; the backend constructs a `VectorSlot` from them
 * and stays graph-agnostic.
 */
export type UpsertEmbeddingParams = Readonly<{
  graphId: string;
  nodeKind: string;
  nodeId: string;
  fieldPath: string;
  embedding: readonly number[];
  dimensions: number;
  metric: VectorMetric;
  indexType: VectorIndexType;
}>;

/**
 * A projection written from the node row's `RETURNING` source by a fused
 * generated-id insert. Identity is deliberately absent: the backend derives
 * graph, kind, and id from the inserted-row CTE, so a sidecar cannot disagree
 * with the node row it accompanies.
 */
export type NodeInsertProjection =
  | Readonly<{
      kind: "embedding";
      fieldPath: string;
      embedding: readonly number[];
      dimensions: number;
      metric: VectorMetric;
      indexType: VectorIndexType;
    }>
  | Readonly<{
      kind: "fulltext";
      action: "upsert";
      content: string;
      language: string;
    }>
  | Readonly<{
      kind: "fulltext";
      action: "delete";
    }>;

/**
 * A uniqueness-relation claim carried by an atomic node-insert plan.
 *
 * Node identity is deliberately absent: the backend derives the owner pair
 * from the node insert parameters, so the claim cannot name a different row.
 * Placement is decided by the store's claim-site owner and preserved by the
 * SQL compiler as an explicit dependency around the node insert.
 */
export type NodeInsertClaimVerdict =
  | Readonly<{
      kind: "uniqueness";
      probeAxes: readonly string[];
      fields: readonly string[];
    }>
  | Readonly<{
      kind: "disjointness";
      conflictingKinds: readonly string[];
    }>;

export type NodeInsertClaim = Readonly<{
  axis: string;
  constraintName: string;
  key: string;
  placement: "pre-insert" | "post-insert";
  /**
   * The complete read set for this claim. A canonical claim upsert only sees
   * the folded axis; the additional uniqueness axes preserve compatibility
   * with rows written before that fold. Disjoint claims name their live-node
   * fallback population instead, because old databases can have no claim row
   * for an existing disjoint overlap.
   */
  verdict: NodeInsertClaimVerdict;
}>;

/** The two atomic node-insert shapes supported by a planned-write backend. */
export type ManagedNodeCreateMode =
  | Readonly<{ kind: "ordinary" }>
  | Readonly<{
      kind: "schema-fenced";
      schemaFence: SchemaWriteFenceParams;
    }>;

/** A complete managed node create, including its row and atomic write plan. */
export type ManagedNodeCreatePlan = Readonly<{
  entity: "node";
  params: InsertNodeParams;
  /** Whether the node id was generated by the store rather than supplied. */
  idGenerated: boolean;
  mode: ManagedNodeCreateMode;
  claims: readonly NodeInsertClaim[];
  projections: readonly NodeInsertProjection[];
}>;

/** A complete managed edge create, including its row and atomic write plan. */
export type ManagedEdgeCreatePlan = Readonly<{
  entity: "edge";
  params: InsertEdgeParams;
  schemaFence?: SchemaWriteFenceParams;
  cardinalityClaim?: ClaimEdgeCardinalityParams;
}>;

/** A node create command accepted by the authoritative command port. */
export type NodeCreateCommand = Readonly<{
  kind: "node.create";
  plan: ManagedNodeCreatePlan;
}>;

/** An edge create command accepted by the authoritative command port. */
export type EdgeCreateCommand = Readonly<{
  kind: "edge.create";
  plan: ManagedEdgeCreatePlan;
}>;

/** The authoritative match-key read paired with a convergent edge create. */
export type EdgeConvergenceMatch =
  | Readonly<{
      kind: "dynamic";
      matchOn: readonly string[];
      props: Record<string, unknown>;
    }>
  | Readonly<{
      kind: "durable";
      identity: EdgeMatchIdentityStorage;
    }>;

/** An edge create command that atomically returns an existing match or inserts. */
export type EdgeConvergeCreateCommand = Readonly<{
  kind: "edge.converge-create";
  plan: ManagedEdgeCreatePlan;
  match: EdgeConvergenceMatch;
}>;

/** The semantic commands currently supported by the authoritative port. */
export type GraphCommand =
  NodeCreateCommand | EdgeCreateCommand | EdgeConvergeCreateCommand;

export type NodeCreateCommandResult =
  | Readonly<{ outcome: "created"; entity: "node"; row: NodeRow }>
  | Readonly<{ outcome: "rejected"; entity: "node"; reason: "unknown" }>
  | Readonly<{
      outcome: "unsupported";
      entity: "node";
      dimensions: readonly [
        "schemaFence" | "claims" | "projections",
        ...(readonly ("schemaFence" | "claims" | "projections")[]),
      ];
    }>;

export type EdgeCreateCommandResult =
  | Readonly<{ outcome: "created"; entity: "edge"; row: EdgeRow }>
  | Readonly<{ outcome: "rejected"; entity: "edge"; reason: "unknown" }>
  | Readonly<{
      outcome: "unsupported";
      entity: "edge";
      dimensions: readonly [
        "schemaFence" | "cardinalityClaim" | "endpointPredicate",
        ...(readonly (
          "schemaFence" | "cardinalityClaim" | "endpointPredicate"
        )[]),
      ];
    }>;

export type EdgeConvergeCreateCommandResult =
  | Readonly<{ outcome: "created"; entity: "edge"; row: EdgeRow }>
  | Readonly<{ outcome: "found"; entity: "edge"; row: EdgeRow }>
  | Readonly<{ outcome: "rejected"; entity: "edge"; reason: "unknown" }>
  | Readonly<{
      outcome: "unsupported";
      entity: "edge";
      dimensions: readonly [
        "convergence" | "endpointPredicate",
        ...(readonly ("convergence" | "endpointPredicate")[]),
      ];
    }>;

/** The session boundary on which an authoritative command executes. */
export type GraphCommandSession = "root" | "transaction";

/** Effective transaction isolation observed on the command's physical session. */
export type GraphCommandIsolation =
  "read_committed" | "repeatable_read" | "serializable" | "unknown";

declare const GRAPH_COMMAND_COORDINATION_BRAND: unique symbol;

/** Compile-time evidence that graph-write coordination is already established. */
export type GraphCommandCoordination = Readonly<{
  [GRAPH_COMMAND_COORDINATION_BRAND]: true;
}>;

export type GraphCommandExecutionContext =
  | Readonly<{
      session: "root";
      coordination: "none";
    }>
  | Readonly<{
      session: "transaction";
      coordination: "none" | GraphCommandCoordination;
    }>;

/** The result union returned by the authoritative command port. */
export type GraphCommandResult =
  | NodeCreateCommandResult
  | EdgeCreateCommandResult
  | EdgeConvergeCreateCommandResult;

/** The single extensible authority boundary for semantic backend commands. */
export type GraphCommandPort = Readonly<{
  /** The database session this port is structurally bound to. */
  session: GraphCommandSession;
  /**
   * Execute an authoritative command. First-party Store paths pass an
   * explicit execution context through
   * `executeAuthoritativeGraphCommand`.
   */
  execute: (
    this: void,
    command: GraphCommand,
    context: GraphCommandExecutionContext,
  ) => Promise<GraphCommandResult>;
}>;

/**
 * One row of a batched embedding upsert.
 */
export type UpsertEmbeddingBatchRow = Readonly<{
  nodeId: string;
  embedding: readonly number[];
}>;

/**
 * Parameters for a batched embedding upsert. Homogeneous per vector slot:
 * one graph, one node kind, one field, many nodes. Duplicate `nodeId`
 * values within a batch are deduplicated last-write-wins by the backend
 * (a multi-row upsert cannot affect one row twice).
 */
export type UpsertEmbeddingBatchParams = Readonly<{
  graphId: string;
  nodeKind: string;
  fieldPath: string;
  dimensions: number;
  metric: VectorMetric;
  indexType: VectorIndexType;
  rows: readonly UpsertEmbeddingBatchRow[];
}>;

/**
 * Parameters for deleting an embedding.
 *
 * `dimensions` / `metric` / `indexType` mirror {@link UpsertEmbeddingParams}
 * so the backend can resolve the slot's typed per-`(nodeKind, fieldPath)`
 * storage and idempotently ensure it exists before the DELETE. That
 * matters because a delete can run before any embedding was ever written
 * (e.g. a node hard-deleted having never carried one), and on Postgres a
 * DELETE against a missing relation inside a transaction aborts the whole
 * transaction. The store populates them from the schema's `embedding()`
 * declaration, exactly as for upserts.
 */
export type DeleteEmbeddingParams = Readonly<{
  graphId: string;
  nodeKind: string;
  nodeId: string;
  fieldPath: string;
  dimensions: number;
  metric: VectorMetric;
  indexType: VectorIndexType;
}>;

/**
 * Parameters for vector similarity search.
 */
export type VectorSearchParams = Readonly<{
  graphId: string;
  nodeKind: string;
  fieldPath: string;
  queryEmbedding: readonly number[];
  metric: VectorMetric;
  /**
   * Fixed vector dimension `N` for the searched `(nodeKind, fieldPath)`
   * slot. The store populates it from the schema's `embedding()`
   * declaration via `getEmbeddingDimensions()`; the backend uses it to
   * construct the `VectorSlot` whose typed storage the strategy scans.
   */
  dimensions: number;
  /**
   * Index type materialized for this slot. `"none"` means brute-force
   * only; otherwise the strategy may route through its ANN structure.
   * The store populates it from `getEmbeddingIndex()`.
   */
  indexType: VectorIndexType;
  limit: number;
  minScore?: number;
  /**
   * Subquery of eligible node ids (single column, exposed as `node_id`).
   * When present, the search statement computes top-k over this candidate
   * set — the store passes its compiled current-read query here so
   * `where` predicates push down into the search SQL. Exact on engines
   * whose search form takes the filter inside retrieval (pgvector,
   * sqlite-vec, tsvector, FTS5); libSQL's DiskANN cannot pre-filter, so
   * it post-filters an over-fetched ANN set and recall against the
   * candidate set is bounded by that headroom. When absent, the backend
   * restricts to CURRENT nodes of the kind — non-tombstoned AND inside
   * their validity window — matching a `current` read.
   */
  candidates?: SqlFragment;
  /**
   * Rows to skip AFTER ranking (pagination). The engine fetches
   * `limit + offset` ranked candidates and discards the first `offset`.
   */
  offset?: number;
  /**
   * HNSW search frontier for this query (pgvector `hnsw.ef_search`).
   * Sizes the dynamic candidate list the index scan maintains: higher
   * trades latency for recall. The floor for the over-fetch to fill its
   * candidate set is `efSearch >= limit`; ~2–4× is the high-recall
   * target. Applied transaction-locally (`SET LOCAL`) on the Postgres
   * HNSW path only.
   *
   * APPLIED OR REFUSED, never ignored. Read
   * `capabilities.vector.searchFrontierTuning` to know which you get:
   *
   * - `tunable: false` (sqlite-vec, libSQL DiskANN — no per-search frontier
   *   knob exists): `UnsupportedBackendCapabilityError`.
   * - a slot whose index type is not the tunable one (pgvector IVFFlat or
   *   brute-force): `ConfigurationError`.
   * - `requiresTransactionScope` on a backend reporting
   *   `execution.interactiveTransactions: false` (for example `drizzle-orm/neon-http`):
   *   `UnsupportedBackendCapabilityError`, because `SET LOCAL` has no frame
   *   to be local to.
   */
  efSearch?: number;
}>;

/**
 * Result from a vector similarity search.
 */
export type VectorSearchResult = Readonly<{
  nodeId: string;
  /**
   * Cosine metric returns similarity score (higher is better).
   * L2 and inner_product return raw distance (lower is better).
   */
  score: number;
}>;

/**
 * Parameters for a single-statement hybrid (vector + fulltext, RRF-fused)
 * search. The store facade resolves every default before calling — the
 * per-source candidate depths (`k`), the fusion constants, and the shared
 * `candidates` subquery — so the backend composes `SqlFragment` values without policy.
 */
export type HybridSearchParams = Readonly<{
  graphId: string;
  nodeKind: string;
  vector: Readonly<{
    fieldPath: string;
    queryEmbedding: readonly number[];
    metric: VectorMetric;
    dimensions: number;
    indexType: VectorIndexType;
    /** Candidate depth of the vector source (rows entering fusion). */
    k: number;
    minScore?: number;
    efSearch?: number;
  }>;
  fulltext: Readonly<{
    query: string;
    mode?: FulltextQueryMode;
    language?: string;
    /** Candidate depth of the fulltext source (rows entering fusion). */
    k: number;
    minScore?: number;
    includeSnippets?: boolean;
  }>;
  fusion: Readonly<{
    /** RRF rank constant (`1 / (k + rank)`). */
    k: number;
    vectorWeight: number;
    fulltextWeight: number;
  }>;
  /** Fused rows to return after `offset`. */
  limit: number;
  /** Fused rows to skip (rank-relative pagination). */
  offset?: number;
  /** See {@link VectorSearchParams.candidates}; applies to both sources. */
  candidates?: SqlFragment;
}>;

/**
 * One fused hybrid hit, hydrated in the same statement: the node row
 * rides along so the caller needs no follow-up id fetch.
 */
export type HybridSearchRow = Readonly<{
  node: NodeRow;
  fusedScore: number;
  vectorRank?: number;
  vectorScore?: number;
  fulltextRank?: number;
  fulltextScore?: number;
  snippet?: string;
}>;

/**
 * Parameters for creating a vector index.
 */
export type CreateVectorIndexParams = Readonly<{
  graphId: string;
  nodeKind: string;
  fieldPath: string;
  dimensions: number;
  metric: VectorMetric;
  indexType: VectorIndexType;
  /** Index-specific parameters */
  indexParams?: Readonly<{
    /** HNSW: max connections per layer */
    m?: number;
    /** HNSW: construction search depth */
    efConstruction?: number;
    /** IVFFlat: number of lists */
    lists?: number;
  }>;
  /**
   * Build the index without taking an `AccessExclusiveLock` on live
   * tables (Postgres `CREATE INDEX CONCURRENTLY`). Mirrors the
   * `concurrent` flag the relational DDL path uses inside
   * `materializeIndexes()`. Cannot be set inside a transaction —
   * callers (`materializeIndexes()`) run at top level. Backends that
   * don't have a CONCURRENTLY equivalent (SQLite) ignore this flag.
   */
  concurrent?: boolean;
}>;

/**
 * Parameters for dropping a vector index.
 */
export type DropVectorIndexParams = Readonly<{
  graphId: string;
  nodeKind: string;
  fieldPath: string;
}>;

// ============================================================
// Fulltext Parameters
// ============================================================

/**
 * Parameters for inserting or updating a fulltext entry.
 *
 * One row per node. Callers concatenate the searchable fields into
 * `content` so a single MATCH query can find terms spread across fields.
 *
 * Note: `createFulltextIndex` / `dropFulltextIndex` were removed as
 * dead code in #PR_E. The fulltext table's canonical index (Postgres
 * GIN on `tsv`, SQLite FTS5 virtual table) is created with the table
 * itself by `bootstrapTables` per the active `FulltextStrategy`;
 * per-kind fulltext indexes are an "advanced strategy" surface that
 * doesn't fit the relational-style declaration model and is reserved
 * for future work.
 */
export type UpsertFulltextParams = Readonly<{
  graphId: string;
  nodeKind: string;
  nodeId: string;
  content: string;
  language: string;
}>;

/** Parameters for the ordinary post-insert fulltext synchronization path. */
export type NodeFulltextSync =
  | Readonly<{
      graphId: string;
      nodeKind: string;
      nodeId: string;
      action: "upsert";
      content: string;
      language: string;
    }>
  | Readonly<{
      graphId: string;
      nodeKind: string;
      nodeId: string;
      action: "delete";
    }>;

/**
 * Parameters for deleting a single fulltext entry.
 */
export type DeleteFulltextParams = Readonly<{
  graphId: string;
  nodeKind: string;
  nodeId: string;
}>;

/**
 * A single row in a fulltext batch upsert.
 */
export type FulltextBatchRow = Readonly<{
  nodeId: string;
  content: string;
  language: string;
}>;

/**
 * Parameters for a batched fulltext upsert.
 *
 * Homogeneous: one graph, one node kind, many nodes. Duplicate `nodeId`
 * values within a single batch are deduplicated last-write-wins by the
 * builders before SQL generation — Postgres `ON CONFLICT` errors on
 * repeated conflict keys in one statement, and SQLite `DELETE + INSERT`
 * would create duplicate virtual-table rows otherwise.
 */
export type UpsertFulltextBatchParams = Readonly<{
  graphId: string;
  nodeKind: string;
  rows: readonly FulltextBatchRow[];
}>;

/**
 * Parameters for a batched fulltext delete.
 */
export type DeleteFulltextBatchParams = Readonly<{
  graphId: string;
  nodeKind: string;
  nodeIds: readonly string[];
}>;

/**
 * Parameters for fulltext search.
 */
export type FulltextSearchParams = Readonly<{
  graphId: string;
  nodeKind: string;
  /** The user-supplied query string. */
  query: string;
  /** How to parse `query`. Default: "websearch". */
  mode?: FulltextQueryMode;
  /**
   * Language for query parsing. The store facade resolves the kind's
   * declared language and passes it here, so the tsquery is a plan-time
   * constant (GIN-servable); when absent the backend falls back to the
   * per-row language column.
   * Postgres: passed as the regconfig to `to_tsquery` / `websearch_to_tsquery`.
   * SQLite: informational only (FTS5 tokenizer is fixed at table-create time).
   */
  language?: string;
  /** Max rows to return. */
  limit: number;
  /** Minimum rank to include (backend-dependent units). */
  minScore?: number;
  /** Whether to return a highlighted snippet per match. */
  includeSnippets?: boolean;
  /**
   * Subquery of eligible node ids (single column). When present, the
   * search statement computes top-k over this candidate set — the store
   * passes its compiled current-read query here so `where` predicates,
   * subclass expansion, and valid-time currency all push down into the
   * search SQL. Exact on engines whose search form takes the filter
   * inside retrieval (pgvector, sqlite-vec, tsvector, FTS5); libSQL's
   * DiskANN cannot pre-filter, so it post-filters an over-fetched ANN
   * set and recall against the candidate set is bounded by that
   * headroom. When absent, the backend restricts to CURRENT nodes of
   * the kind — non-tombstoned AND inside their validity window —
   * matching a `current` read.
   */
  candidates?: SqlFragment;
  /**
   * Rows to skip AFTER ranking (pagination). The engine fetches
   * `limit + offset` ranked candidates and discards the first `offset`.
   */
  offset?: number;
}>;

/**
 * Result from a fulltext search.
 *
 * Score semantics differ by backend; prefer `rank` (1-based) when fusing
 * with another source via RRF.
 */
export type FulltextSearchResult = Readonly<{
  nodeId: string;
  /**
   * Backend-native relevance score.
   * Postgres: `ts_rank_cd` (higher is better).
   * SQLite FTS5: negated `bm25()` (higher is better; FTS5 returns lower-is-better).
   */
  score: number;
  /** 1-based rank within the result set, suitable for RRF. */
  rank: number;
  /** Highlighted snippet of the content (if `includeSnippets` was set). */
  snippet?: string;
}>;

/**
 * Parameters for creating a fulltext index.
 *
 * The canonical index (Postgres GIN on `tsv`, SQLite FTS5 virtual table)
 * is created when the fulltext table itself is created. This is reserved
 * for advanced per-kind specializations.
 */

// ============================================================
// Index Materialization Status
// ============================================================

/**
 * Per-deployment record for a single declared index.
 *
 * Identified by `indexName` because SQL index names are physical,
 * database-global identifiers — `graphId` is provenance, not identity.
 * `materializedAt` is null until the first successful CREATE INDEX
 * completes; `lastAttemptedAt` is always set, even on failure.
 */
/**
 * Parameters for atomically claiming an index build (see
 * `claimIndexMaterialization`). The declaration fields ride along so a
 * fresh claim row is honest while the build is in flight.
 */
export type ClaimIndexMaterializationParams = Readonly<{
  indexName: string;
  graphId: string;
  entity: IndexEntity;
  kind: string;
  signature: string;
  schemaVersion: number;
  /** Opaque claim identity; release is token-guarded. */
  token: string;
  /** A claim older than this is stale and may be taken over. */
  leaseMs: number;
}>;

/** Parameters for releasing a build claim (token-guarded). */
export type ReleaseIndexMaterializationClaimParams = Readonly<{
  indexName: string;
  token: string;
}>;

export type IndexMaterializationRow = Readonly<{
  indexName: string;
  graphId: string;
  entity: IndexEntity;
  kind: string;
  signature: string;
  schemaVersion: number;
  materializedAt: string | undefined;
  lastAttemptedAt: string;
  lastError: string | undefined;
}>;

/**
 * Parameters for upserting a materialization attempt.
 *
 * On success: pass `materializedAt` (ISO timestamp) and undefined `error`.
 * On failure: pass undefined `materializedAt` (preserve any existing
 * timestamp from a prior success) and the error message.
 */
export type RecordIndexMaterializationParams = Readonly<{
  indexName: string;
  graphId: string;
  entity: IndexEntity;
  kind: string;
  signature: string;
  schemaVersion: number;
  attemptedAt: string;
  /** ISO timestamp on success; undefined on failure (preserves existing). */
  materializedAt: string | undefined;
  /** Error message on failure; undefined on success (clears existing). */
  error: string | undefined;
}>;

// ============================================================
// Contribution Materializations (#135 — durable strategy-owned
// storage marker, sibling of index materializations)
// ============================================================

/**
 * Durable identity of a strategy-owned table contribution (#129),
 * scoped to a graph. This is the primary key of
 * `typegraph_contribution_materializations`.
 *
 * `logicalName` is the stable slot ("fulltext"); `owner` is the
 * producing strategy ("tsvector" / "fts5"); `tableName` is the resolved
 * physical name (custom per-deployment names must be distinguishable).
 * `graphId` is part of identity here — unlike the index status table
 * where the physical index name is database-global, two graphs can each
 * own a logically-identical fulltext contribution.
 */
export type ContributionMaterializationIdentity = Readonly<{
  graphId: string;
  logicalName: string;
  owner: string;
  tableName: string;
}>;

/**
 * Per-deployment record that a strategy-owned contribution has been
 * durably materialized against this database.
 *
 * `signature` is intentionally NOT part of the identity: a row with the
 * same identity but a different signature means "materialized artifact
 * is stale/drifted" — a loud error on the hot path, never a silent
 * re-materialize. `materializedAt` is undefined until the first
 * successful materialization; `lastAttemptedAt` is always set.
 */
export type ContributionMaterializationRow = Readonly<{
  graphId: string;
  logicalName: string;
  owner: string;
  tableName: string;
  signature: string;
  materializedAt: string | undefined;
  lastAttemptedAt: string;
  lastError: string | undefined;
}>;

/**
 * Parameters for upserting a contribution-materialization attempt.
 * Same success/failure contract as
 * {@link RecordIndexMaterializationParams}: on failure pass undefined
 * `materializedAt` so a prior successful timestamp is preserved via
 * COALESCE, and the error message.
 */
export type RecordContributionMaterializationParams = Readonly<{
  graphId: string;
  logicalName: string;
  owner: string;
  tableName: string;
  signature: string;
  attemptedAt: string;
  /** ISO timestamp on success; undefined on failure (preserves existing). */
  materializedAt: string | undefined;
  /** Error message on failure; undefined on success (clears existing). */
  error: string | undefined;
}>;

/**
 * Why a contribution is not usable. Most members are a disagreement
 * between the durable marker and the physical catalog; one
 * (`failed-materialization`) is a state the two agree on and that is
 * broken anyway. A contribution that is genuinely healthy — or that was
 * simply never attempted — is not reported at all.
 *
 * - `orphaned-marker` — the marker records a successful materialization
 *   but the physical table is gone (a partial restore, an out-of-band
 *   `DROP`, a schema-scoped restore that missed the contribution
 *   tables). The state {@link GraphBackend.verifyContributions} exists
 *   to surface: nothing on the open path probes the catalog, so this
 *   database opens clean and fails at the first dependent read or write.
 * - `missing-marker` — the physical table exists but no marker attests
 *   it as initialized (no row, no recorded success, or a recorded
 *   failure). Reads and writes are refused with
 *   `StoreNotInitializedError` even though storage is present. These
 *   three causes share one state because they share one repair; check
 *   {@link ContributionDiagnostic.lastError} to tell them apart.
 * - `failed-materialization` — the marker records a *failed* attempt
 *   and no table was produced. Marker and catalog agree here, so this
 *   is not a disagreement — but it is where a contribution lands when
 *   provisioning genuinely broke (the `fts5` module is absent, the role
 *   lacks `CREATE`, the extension was never loaded), and calling it
 *   healthy on the grounds that both sides agree would be the one
 *   answer this method must never give. Distinguished from a
 *   contribution that was never attempted, which has no marker row and
 *   is correctly silent. {@link ContributionDiagnostic.lastError}
 *   carries the reason it failed.
 * - `stale` — the table exists and the marker records a prior success
 *   at a different signature (a strategy swap, or a declared embedding
 *   dimension that has moved ahead of the provisioned table).
 *
 * **Do not re-frame this union as "how the marker disagrees with the
 * catalog."** It was described that way once, and the description was
 * load-bearing in the wrong direction: under it, a marker recording a
 * failed attempt with no table looks like agreement, therefore not a
 * disagreement, therefore correctly silent — and a contribution that
 * had genuinely broken was reported as healthy. The gap read as
 * principled rather than as an oversight precisely because the framing
 * endorsed it. "Why the contribution is unusable" is the question that
 * makes `failed-materialization` obviously belong, and any future
 * tidy-up that narrows the framing back will re-open the same hole.
 */
export type ContributionDiagnosticState =
  "orphaned-marker" | "missing-marker" | "failed-materialization" | "stale";

/**
 * One contribution that is not usable, reported by
 * {@link GraphBackend.verifyContributions}.
 *
 * The identity fields come from the active contribution declaration and
 * match the durable-marker identity contract, so a caller can route an entry
 * to its repair without reconstructing any internal naming contract. A
 * `missing-marker` entry may have no marker row at all.
 *
 * **Route on {@link ContributionDiagnosticState}, not on whether the
 * entry is a vector slot.** The repair differs per state and the wrong
 * choice destroys data: `store.reembedVectorField` drops and recreates
 * storage, so applying it to a `missing-marker` — where the table is
 * intact and only the bookkeeping is wrong — discards every embedding
 * to fix a marker, and without an `embed` callback it leaves the field
 * empty. Prefer `store.repairContributions()` for `missing-marker` and
 * `failed-materialization`; it resolves the current strategy declarations
 * internally and keeps the normal signature-drift guard enabled. It reports
 * `stale` and `orphaned-marker` as `requires-rebuild`. See the per-state
 * repair table in the troubleshooting guide.
 */
export type ContributionDiagnostic = Readonly<{
  /** Producing strategy, e.g. `"fts5"` / `"tsvector"` / `"pgvector"`. */
  owner: string;
  /** Stable logical slot, e.g. `"fulltext"`. Never the SQL name. */
  logicalName: string;
  /** Resolved physical table name that was probed. */
  physicalName: string;
  /** Node kind — vector slots only. */
  kind?: string;
  /** Embedding field path — vector slots only. */
  fieldPath?: string;
  state: ContributionDiagnosticState;
  /**
   * The error the marker recorded against its last attempt, when it
   * recorded one. Absent otherwise.
   *
   * This is the part of the picture the catalog cannot supply. `state`
   * says what to do — the states are chosen so that each maps to one
   * repair — while this says *why it broke*, which is a different
   * question and often a different investigation. A failed attempt with no
   * table surfaces as `failed-materialization`; when the table exists but
   * the marker does not attest a successful materialization, it surfaces as
   * `missing-marker`. This field distinguishes only failures whose marker
   * recorded a reason; a missing row has no `lastError`. Present on any state
   * whose marker row carries an error, not just those two states.
   */
  lastError?: string;
}>;

/**
 * Outcome of one contribution considered by
 * {@link GraphBackend.repairContributions}.
 *
 * Repair targets are always resolved from the backend's current strategy
 * declarations. The diagnostic is returned for operator context only; callers
 * never pass diagnostics or physical DDL back into the repair API.
 */
export type ContributionRepairEntry =
  | Readonly<{
      diagnostic: ContributionDiagnostic;
      status: "repaired";
    }>
  | Readonly<{
      diagnostic: ContributionDiagnostic;
      status: "requires-rebuild";
    }>
  | Readonly<{
      diagnostic: ContributionDiagnostic;
      status: "failed";
      error: string;
    }>;

/** Result of a contribution repair pass followed by a fresh verification. */
export type ContributionRepairResult = Readonly<{
  results: readonly ContributionRepairEntry[];
  remaining: readonly ContributionDiagnostic[];
}>;

// ============================================================
// Contribution readiness probe (#377)
// ============================================================

/**
 * The search projection a probe entry summarizes. Deliberately the
 * logical *class* rather than one physical table: a caller asking "is
 * search ready?" is deciding whether to issue a fulltext or a vector
 * query, and a per-`(kind, field)` breakdown of pgvector tables answers
 * a question they did not ask. The physical detail an operator needs to
 * act is one `verifyContributions()` call away, and
 * {@link ContributionProbeEntry.detail} names the affected slots.
 */
export type ContributionProbeContribution = "fulltext" | "vector";

/**
 * Readiness of one search projection.
 *
 * - `ready` — every contribution backing this projection is attested by
 *   its durable marker and present in the catalog. Not a promise about
 *   future coherence: a write that lands after the probe returns is
 *   outside what any assessment can cover.
 * - `degraded` — at least one contribution is unusable. Dependent operations
 *   may be refused with a typed contribution error, fail at the engine boundary
 *   when compiled SQL directly references missing storage, or observe
 *   incomplete storage.
 *   {@link ContributionProbeEntry.detail} says which and why.
 * - `building` — **reserved.** No shipped path publishes it. Recording
 *   an in-flight marker would need a fifth
 *   {@link ContributionDiagnosticState} and would widen the hot-path
 *   gate's verdict set; the destructive rebuild instead runs inside one
 *   transaction, so a concurrent probe observes the state before or
 *   after it and never a partial one. Reserved rather than removed so a
 *   future streaming/async materializer can populate it without a
 *   breaking change — treat it as "not `ready`" today.
 */
export type ContributionProbeState = "ready" | "degraded" | "building";

/**
 * One search projection's readiness, from
 * {@link GraphBackend.probeContributions}.
 */
export type ContributionProbeEntry = Readonly<{
  contribution: ContributionProbeContribution;
  state: ContributionProbeState;
  /**
   * Operator-facing summary of why the projection is not `ready` —
   * e.g. `"fulltext table \"typegraph_node_fulltext\" is missing
   * (orphaned-marker)"`. Absent on a `ready` entry.
   *
   * Human-readable and not a stable format: route on `state`, and call
   * `store.verifyContributions()` for the structured per-table
   * diagnostics this string is derived from.
   */
  detail?: string;
}>;

/**
 * Result of {@link GraphBackend.probeContributions}.
 *
 * A projection with no declared contributions is omitted rather than
 * reported `ready`, so an empty `entries` array means "nothing to
 * assess" — a graph with no `searchable()` or `embedding()` fields, or a
 * backend with no contribution support — never "assessed and healthy".
 */
export type ContributionProbeResult = Readonly<{
  /**
   * The durable graph revision the assessment was taken at, so a caller
   * can place this probe in the graph's committed history.
   *
   * Graph-global, like the clock it reads: it advances on every committed
   * capture from any writer, so an advance between two probes means
   * "something committed in between", never "the write this caller just
   * made landed". Confirm a specific write by observing the write itself.
   *
   * Absent unless the Store is revision-tracked (`revisionTracking:
   * true` or `history: true`) — the same condition under which
   * `store.revisionNow()` returns a value. This is the one honest shape:
   * a store with no durable revision clock has no revision to stamp, and
   * substituting a wall-clock timestamp or the schema version would be a
   * materially weaker guarantee wearing the name of a stronger one. The
   * schema version in particular does not advance on data writes, so it
   * could not order a probe against a caller's write at all.
   */
  graphRevision?: string;
  entries: readonly ContributionProbeEntry[];
}>;

// ============================================================
// Destructive contribution rebuild (#337)
// ============================================================

/**
 * Which search projection {@link GraphBackend.rebuildContribution}
 * targets. Scoped explicitly at the call site rather than inferred from
 * a diagnostic: the operation destroys storage, so which storage is the
 * caller's decision to state, not TypeGraph's to guess.
 */
export type ContributionRebuildScope = ContributionProbeContribution;

/**
 * What a rebuild's `repopulate` callback reconstructed. Reported back so
 * the rebuild result can distinguish "recreated empty storage" from
 * "recreated and refilled", which is the difference between a finished
 * repair and half of one.
 */
export type ContributionRepopulationStats = Readonly<{
  /** Nodes scanned. */
  processed: number;
  /** Content rows written. */
  repopulated: number;
  /** Nodes whose stored `props` could not be read as an object. */
  skipped: number;
}>;

/** Outcome of one destructive contribution rebuild. */
export type ContributionRebuildResult = Readonly<{
  /** Physical tables dropped and recreated, in the order rebuilt. */
  rebuilt: readonly string[];
  /** Nodes scanned while reconstructing content from stored rows. */
  processed: number;
  /** Content rows written into the recreated storage. */
  repopulated: number;
  /**
   * Nodes whose stored `props` could not be read as an object, so no
   * content row could be reconstructed for them. Non-zero means the
   * rebuilt index is missing those nodes; the IDs come back from
   * `store.search.rebuildFulltext()`, which reports them individually.
   */
  skipped: number;
}>;

// ============================================================
// Kind Removals (data-cleanup status)
// ============================================================

/**
 * One row of the per-deployment `typegraph_kind_removals` table:
 * a graph-extension kind that has been removed from the schema and whose
 * data may or may not have been cleaned up yet.
 */
export type KindRemovalRow = Readonly<{
  graphId: string;
  kindName: string;
  entity: KindEntity;
  schemaVersion: number;
  /** ISO timestamp when the data-cleanup pass succeeded; undefined while pending. */
  removedAt: string | undefined;
  lastAttemptedAt: string;
  lastError: string | undefined;
}>;

/**
 * Upsert payload for a kind-removal status row. On removal-commit:
 * pass `removedAt: undefined` (the pending state). On successful
 * data-cleanup: pass `removedAt` set and `error: undefined`. On
 * cleanup failure: pass `removedAt: undefined` (preserves any prior
 * timestamp from a partial success on a different replica) and the
 * error message.
 */
export type RecordKindRemovalParams = Readonly<{
  graphId: string;
  kindName: string;
  entity: KindEntity;
  schemaVersion: number;
  attemptedAt: string;
  /** ISO timestamp on success; undefined while pending or on failure. */
  removedAt: string | undefined;
  /** Error message on failure; undefined on success or while pending. */
  error: string | undefined;
}>;

// ============================================================
// Query Types
// ============================================================

/** @internal Capability token for connection-local temporary writes. */
export const INTERNAL_TEMPORARY_WRITES: unique symbol = typeGraphGlobalSymbol(
  "internal-temporary-writes-v1",
);

/**
 * Transaction options.
 */
export type TransactionOptions = Readonly<{
  /** Transaction isolation level (if supported) */
  isolationLevel?:
    "read_uncommitted" | "read_committed" | "repeatable_read" | "serializable";
  /**
   * Transaction access mode (if supported). `read_only` is intended for
   * multi-statement reads that need one snapshot and must not perform writes.
   */
  accessMode?: "read_only" | "read_write";
}>;

/** @internal Transaction options available only to TypeGraph-owned execution. */
export type InternalTransactionOptions = TransactionOptions &
  Readonly<{
    /**
     * Permit writes only to connection-local temporary state. The iterative
     * operation primitive uses this when an engine rejects temporary DDL in a
     * read-only transaction.
     */
    temporaryWrites?: typeof INTERNAL_TEMPORARY_WRITES;
  }>;

// ============================================================
// Backend Interface
// ============================================================

/**
 * The physical names of the four Operational Identity relations: the current
 * assertion ledger, its recorded-time twin, and the two DERIVED relations
 * (closure, separation) rebuilt from the ledger.
 *
 * Named once so the two ports that speak about them —
 * {@link GraphBackend.ensureIdentityTables} and
 * {@link GraphBackend.identityTableDdl} — cannot drift apart.
 */
export type IdentityTableNames = Readonly<{
  identityAssertions: string;
  recordedIdentityAssertions: string;
  identityClosure: string;
  identitySeparation: string;
}>;

/**
 * The physical names of the three recorded relations. Named once so the
 * ports that speak about them cannot drift apart — the {@link IdentityTableNames}
 * precedent.
 */
export type RecordedTableNames = Readonly<{
  recordedClock: string;
  recordedEdges: string;
  recordedNodes: string;
}>;

/**
 * One recorded relation's provisioning DDL, with the roles the caller needs
 * SEPARATED rather than positional.
 */
export type RecordedRelationDdl = Readonly<{
  /** The single `CREATE TABLE` statement. */
  createTable: string;
  /** Its index/constraint statements, in application order. */
  indexes: readonly string[];
  /**
   * The name the ENGINE will have given this relation's PRIMARY KEY constraint
   * once `createTable` has executed, or `undefined` on an engine that does not
   * name PK constraints separately (SQLite).
   *
   * NOT "the name the DDL text declares": both bundled emitters push an unnamed
   * inline `PRIMARY KEY (…)` (`ddl.ts:114-118`, `:441-445`), so on PostgreSQL the
   * server derives `<table>_pkey`. The point of the field is that the derivation
   * is an ENGINE convention, and the only honest source of it is whoever authored
   * the DDL for that engine — a migration that reconstructs it bakes one adapter's
   * convention in.
   *
   * The name is returned UNREDUCED. Reducing an over-long identifier is a
   * separate decision with a separate owner (`shortenedIdentifier`), and it
   * applies only to the RENAME's target. The source name is the *temporary*
   * table's PK name, and temp table names are hash-derived and short BY
   * CONSTRUCTION (`` `__tg_${role}_${shortHash(tableName)}` ``, an 8-char
   * hash, so `<temp>_pkey` is at most ~21 bytes for any configured table
   * name) — nowhere near the 63-byte ceiling. So the split is a structural
   * fact about the temp-name shape, not a claim about PostgreSQL's
   * truncation semantics: `shortenedIdentifier` is the identity on the
   * source for every reachable input, and its effect is observable only on
   * the target.
   *
   * A backend must apply this option consistently across name sets: return a
   * name for both the temporary and final relation, or `undefined` for both.
   * The migration refuses a mixed pair because silently dropping either name
   * would leave the swapped relation with a backend-inconsistent constraint.
   */
  primaryKeyConstraintName?: string | undefined;
}>;

/**
 * The database-global extensions TypeGraph installs on a caller's behalf.
 *
 * The extent is closed on purpose: {@link GraphBackend.ensureExtension}
 * interpolates the name into DDL, so the allowlist — not the caller — is what
 * decides the identifier can be trusted. `pg_trgm` backs `method: "trigram"`
 * index materialization; `vector` backs pgvector storage.
 */
export const DATABASE_EXTENSION_NAMES = ["pg_trgm", "vector"] as const;

/** A name {@link GraphBackend.ensureExtension} accepts. */
export type DatabaseExtensionName = (typeof DATABASE_EXTENSION_NAMES)[number];

/**
 * Optional durable-identity batch write seam. Every input must carry
 * `matchIdentity`; identity conflicts are omitted from the returned rows.
 */
export type DurableEdgeBatchMembers = Readonly<{
  insertEdgesDurableBatchReturning?: (
    this: void,
    params: readonly InsertEdgeParams[],
  ) => Promise<readonly EdgeRow[]>;
}>;

/**
 * The GraphBackend interface abstracts database operations.
 *
 * Implementations should provide:
 * - SQLite backend via better-sqlite3 or libsql
 * - PostgreSQL backend via pg or postgres
 *
 * Every function is receiver-free (`this: void`). Implementations must close
 * over their state instead of reading `this`, which makes saved optional
 * capabilities and other detached port calls safe by construction.
 */
export type GraphBackend = Readonly<{
  /** The SQL dialect */
  dialect: SqlDialect;
  /** Backend capabilities */
  capabilities: BackendCapabilities;
  /** Table names used by this backend (for query schema auto-derivation) */
  tableNames?: SqlTableNames | undefined;
  /**
   * Optional fulltext strategy override. When present, both the compiler
   * (for `$fulltext.matches()` in query builder) and backend-direct
   * search paths use this instead of the dialect's default strategy —
   * allowing a Postgres backend to ship pg_trgm, ParadeDB, pgroonga etc.
   * When absent, the dialect's default strategy is used.
   */
  fulltextStrategy?: FulltextStrategy | undefined;
  /**
   * Optional vector strategy this backend is wired with. The query
   * compiler reads it (via `compileQuery` options) to emit per-`(kind,
   * field)` relevance scans for `field.similarTo(...)` predicates, and
   * the index-materialization / removal paths read its deterministic
   * `tableName(...)` to address the right physical per-field storage.
   * `undefined` when the backend has no vector support (e.g. a generic
   * SQLite backend without sqlite-vec).
   */
  vectorStrategy?: VectorStrategy | undefined;
  /**
   * The lock-statement spelling this backend's `capabilities.writeFence`
   * declaration requires when `mechanism` is `"advisory"` — resolved through
   * {@link resolveWriteFencePlan} rather than read directly by a lock site.
   * Absent on a backend that serializes writers instead (SQLite's writer
   * slot needs no lock statement at all) or that declares no usable fence.
   */
  fenceSql?: FenceSql | undefined;

  // === Node Operations ===
  insertNode: (this: void, params: InsertNodeParams) => Promise<NodeRow>;
  /**
   * Inserts a node only when its primary-key slot is empty. `undefined` means
   * the slot was already occupied; unlike `insertNode`, that outcome is not a
   * database error and leaves the surrounding transaction usable.
   *
   * First-party SQL backends expose this for the caller-supplied-id create
   * fast path. Custom backends may omit it and retain the probe-then-insert
   * path.
   */
  insertNodeIfAbsent?: (
    this: void,
    params: InsertNodeParams,
  ) => Promise<NodeRow | undefined>;
  /**
   * First-party transactional fast path: the INSERT takes the active-schema
   * shared fence in its source query. A missing row is deliberately ambiguous
   * (stale schema versus an occupied id) and must be diagnosed by the caller.
   */
  insertNodeIfAbsentWithSchemaFence?: (
    this: void,
    params: InsertNodeParams,
    schemaFence: SchemaWriteFenceParams,
  ) => Promise<NodeRow | undefined>;
  /** Like `insertNode`, but acquires the schema fence in that INSERT. */
  insertNodeWithSchemaFence?: (
    this: void,
    params: InsertNodeParams,
    schemaFence: SchemaWriteFenceParams,
  ) => Promise<NodeRow | undefined>;
  insertNodeNoReturn?: (this: void, params: InsertNodeParams) => Promise<void>;
  insertNodesBatch?: (
    this: void,
    params: readonly InsertNodeParams[],
  ) => Promise<void>;
  insertNodesBatchReturning?: (
    this: void,
    params: readonly InsertNodeParams[],
  ) => Promise<readonly NodeRow[]>;
  updateNode: (this: void, params: UpdateNodeParams) => Promise<NodeRow>;
  /** One data-modifying CTE for plain caller-ID node upserts across kinds. */
  upsertHeterogeneousNodes?: (
    this: void,
    params: HeterogeneousNodeUpsertParams,
  ) => Promise<readonly NodeRow[]>;
  updateNodeSet?: (
    this: void,
    params: UpdateNodeSetParams,
  ) => Promise<UpdateNodeSetResult>;
  updateResolvedNodesBatch?: (
    this: void,
    params: ResolvedNodeUpdateBatchParams,
  ) => Promise<readonly NodeRow[]>;
  compareAndSetNode?: (
    this: void,
    params: CompareAndSetNodeParams,
  ) => Promise<UpdateNodeSetResult>;
  deleteNode: (this: void, params: DeleteNodeParams) => Promise<void>;
  hardDeleteNode: (this: void, params: HardDeleteNodeParams) => Promise<void>;
  getNode: (
    this: void,
    graphId: string,
    kind: string,
    id: string,
  ) => Promise<NodeRow | undefined>;
  getNodes?: (
    this: void,
    graphId: string,
    kind: string,
    ids: readonly string[],
  ) => Promise<readonly NodeRow[]>;

  // === Edge Operations ===
  insertEdge: (this: void, params: InsertEdgeParams) => Promise<EdgeRow>;
  /** Executes semantic authoritative graph commands. */
  commands: GraphCommandPort;
  insertEdgeNoReturn?: (this: void, params: InsertEdgeParams) => Promise<void>;
  insertEdgesBatch?: (
    this: void,
    params: readonly InsertEdgeParams[],
  ) => Promise<void>;
  insertEdgesBatchReturning?: (
    this: void,
    params: readonly InsertEdgeParams[],
  ) => Promise<readonly EdgeRow[]>;
  updateEdge: (this: void, params: UpdateEdgeParams) => Promise<EdgeRow>;
  deleteEdge: (this: void, params: DeleteEdgeParams) => Promise<void>;
  hardDeleteEdge: (this: void, params: HardDeleteEdgeParams) => Promise<void>;
  /**
   * Batched {@link deleteEdge}: one soft-delete statement per bind-budget
   * chunk instead of one per edge. Optional — cascade deletes fall back to
   * the per-edge form when unset. Same per-row semantics, including an
   * optional asserted kind and idempotence on already-tombstoned rows.
   */
  deleteEdgesBatch?: (
    this: void,
    params: DeleteEdgesBatchParams,
  ) => Promise<void>;
  /** Batched {@link hardDeleteEdge}; see {@link deleteEdgesBatch}. */
  hardDeleteEdgesBatch?: (
    this: void,
    params: DeleteEdgesBatchParams,
  ) => Promise<void>;
  getEdge: (
    this: void,
    graphId: string,
    id: string,
  ) => Promise<EdgeRow | undefined>;
  getEdges?: (
    this: void,
    graphId: string,
    ids: readonly string[],
  ) => Promise<readonly EdgeRow[]>;

  // === Edge Cardinality Operations ===
  countEdgesFrom: (this: void, params: CountEdgesFromParams) => Promise<number>;
  edgeExistsBetween: (
    this: void,
    params: EdgeExistsBetweenParams,
  ) => Promise<boolean>;

  // === Edge Query Operations ===
  findEdgesConnectedTo: (
    this: void,
    params: FindEdgesConnectedToParams,
  ) => Promise<readonly EdgeRow[]>;

  // === Collection Query Operations ===
  findNodesByKind: (
    this: void,
    params: FindNodesByKindParams,
  ) => Promise<readonly NodeRow[]>;
  countNodesByKind: (
    this: void,
    params: CountNodesByKindParams,
  ) => Promise<number>;
  findEdgesByKind: (
    this: void,
    params: FindEdgesByKindParams,
  ) => Promise<readonly EdgeRow[]>;
  /**
   * Reads the edges of a SET of endpoints in one statement per bind-budget
   * chunk — see {@link FindEdgesByEndpointSetParams}.
   *
   * **Optional, and its absence is the capability signal.** `store.edges.<kind>
   * .bulkFindFrom` / `.bulkFindTo` check for this method before issuing any
   * read and refuse with a typed `ConfigurationError` when it is missing,
   * rather than degrading to a per-endpoint loop. A caller reaching for a bulk
   * endpoint read is asking for set-oriented statements; quietly giving them N
   * singleton statements is the surprise the method exists to prevent.
   *
   * Both bundled Drizzle backends implement it. A custom backend that does not
   * simply omits it and the bulk reads refuse; the singleton `findEdgesByKind`
   * path is unaffected.
   */
  findEdgesByEndpointSet?: (
    this: void,
    params: FindEdgesByEndpointSetParams,
  ) => Promise<readonly EdgeRow[]>;
  /**
   * Reads several edge kinds from a heterogeneous set of endpoints without
   * issuing one statement per licensed `(edge kind, endpoint kind)` pair.
   *
   * Optional for custom backends. Store-level heterogeneous bulk reads refuse
   * when it is absent rather than hiding a per-kind/per-endpoint fallback.
   */
  findEdgesByHeterogeneousEndpointSet?: (
    this: void,
    params: FindEdgesByHeterogeneousEndpointSetParams,
  ) => Promise<readonly EdgeRow[]>;
  countEdgesByKind: (
    this: void,
    params: CountEdgesByKindParams,
  ) => Promise<number>;

  // === Unique Constraint Operations ===
  insertUnique: (this: void, params: InsertUniqueParams) => Promise<void>;
  /**
   * Batched variant of `insertUnique`: one multi-row statement per chunk
   * with the same per-entry conflict semantics (throws `UniquenessError`
   * for the first entry whose key a different live node holds). Optional —
   * callers fall back to per-entry `insertUnique` when unset.
   */
  insertUniqueBatch?: (
    this: void,
    entries: readonly InsertUniqueParams[],
  ) => Promise<void>;
  deleteUnique: (this: void, params: DeleteUniqueParams) => Promise<void>;
  /**
   * Permanently removes every uniqueness sidecar owned by the specified
   * concrete nodes. Optional capability used by set-based updates before they
   * rebuild reservations from the returned node after-images.
   */
  hardDeleteUniquesByNodeIds?: (
    this: void,
    params: HardDeleteUniquesByNodeIdsParams,
  ) => Promise<void>;
  /**
   * Permanently removes every uniqueness claim owned by nodes of one concrete
   * kind, at whatever axis each claim sits on. THE definition of "the claims
   * this kind owns" — kind removal reaps through it so it can neither leak a
   * claim whose axis is a sibling kind nor delete a surviving sibling's claim.
   * Optional capability.
   */
  hardDeleteUniquesByConcreteKind?: (
    this: void,
    params: HardDeleteUniquesByConcreteKindParams,
  ) => Promise<void>;
  checkUnique: (
    this: void,
    params: CheckUniqueParams,
  ) => Promise<UniqueRow | undefined>;
  checkUniqueBatch?: (
    this: void,
    params: CheckUniqueBatchParams,
  ) => Promise<readonly UniqueRow[]>;

  // === Edge Cardinality Claim Operations ===
  /**
   * Takes the claim on one edge cardinality axis, in two statements: a
   * decision-free create-or-lock that reports the committed holder, and — only
   * when that holder is a different edge — a conditional takeover that succeeds
   * exactly when the incumbent is no longer an edge the axis and key describe.
   *
   * The claim is the FENCE for a declared cardinality: `(kind, from)` and
   * `(kind, from, to)` are predicates the edges primary key `(graph_id, id)`
   * cannot enforce, so without this relation two concurrent writers can both
   * pass the probe and both commit. Read through the `constraintClaims`
   * capability, never through member presence — see `claimSupport`
   * (`store/claims/backing.ts`).
   */
  claimEdgeCardinality?: (
    this: void,
    params: ClaimEdgeCardinalityParams,
  ) => Promise<EdgeClaimOutcome>;
  /**
   * Strong single-claim variant which, while holding the claim row, also
   * refuses any claimless live edge that already matches the declared axis.
   *
   * Member presence is an explicit optimization contract: callers may omit
   * the separate entity-relation cardinality probe only when the exact write
   * target exposes this operation. Custom and legacy backends that implement
   * only `claimEdgeCardinality` retain the probe-first path unchanged.
   */
  claimEdgeCardinalityGuarded?: (
    this: void,
    params: ClaimEdgeCardinalityParams,
  ) => Promise<EdgeClaimOutcome>;
  /**
   * Batched variant of `claimEdgeCardinality`: one multi-row create-or-lock
   * statement, then a takeover statement only for the entries a different edge
   * holds. Outcomes are returned positionally, one per entry.
   *
   * Callers must not pass two entries with the same conflict target
   * (`axis`, `key`): a multi-row upsert cannot affect one row twice.
   */
  claimEdgeCardinalityBatch?: (
    this: void,
    entries: readonly ClaimEdgeCardinalityParams[],
  ) => Promise<readonly EdgeClaimOutcome[]>;
  /**
   * Housekeeping only: drops the claim rows named edges hold, so hard deletes,
   * kind removal and `clearGraph` do not grow the relation without bound. The
   * FENCE never depends on it having run — a claim whose holder is no longer
   * live (or, for `oneActive`, no longer active) is taken over in place.
   */
  purgeEdgeClaims?: (
    this: void,
    params: PurgeEdgeClaimsParams,
  ) => Promise<void>;

  /**
   * Read-only diagnostic: the rows that make a declared constraint currently
   * violated, for a caller (`store.verifyConstraintFences()`) that folds them
   * onto the claim axes they contend for.
   *
   * It reads the ENTITY relations for the two families whose pre-upgrade
   * violations no claim row records — a database written before the claim
   * relations existed holds no edge claims at all — and the `uniques` relation
   * for uniqueness, where a pre-upgrade duplicate is exactly two live rows
   * sitting at two different `node_kind`s. A scan of a claim relation's primary
   * key can find neither, which is why this is not one.
   *
   * Returns the contending ROWS, not the verdict: the axis a `uniques` row
   * belongs to is a fold over the graph's subclass component, and the key an
   * edge's claim sits on is `EDGE_CARDINALITY_SPECS`' — both of which live
   * above the backend, so a backend that decided either would be a second
   * spelling of a decision the fence already owns.
   *
   * Writes nothing: no DDL, no claim writes. Safe on a replica and under a
   * least-privilege role. Present only on backends that can run the audit;
   * `store.verifyConstraintFences()` refuses with a typed error when absent
   * rather than reporting an empty (and therefore reassuring) result.
   */
  readConstraintFenceViolations?: (
    this: void,
    params: ReadConstraintFenceViolationsParams,
  ) => Promise<ConstraintFenceViolationRows>;

  // === Schema Operations ===
  getActiveSchema: (
    this: void,
    graphId: string,
  ) => Promise<SchemaVersionRow | undefined>;
  getSchemaVersion: (
    this: void,
    graphId: string,
    version: number,
  ) => Promise<SchemaVersionRow | undefined>;
  /**
   * Atomically inserts a new schema version and activates it as a single
   * transactional unit, with optimistic compare-and-swap on the currently
   * active version.
   *
   * - If `expected.kind === "active"` and the actual active version
   *   differs, throws `StaleVersionError` (caller should refetch and
   *   retry).
   * - If a row already exists at `params.version` with the same
   *   `schemaHash`, returns it idempotently — reactivating it if it was
   *   left inactive by an earlier crashed commit.
   * - If a row already exists at `params.version` with a different
   *   `schemaHash`, throws `SchemaContentConflictError`.
   *
   * Requires `capabilities.execution.interactiveTransactions === true`. On non-transactional
   * backends (e.g. Cloudflare D1, drizzle-orm/neon-http) this method
   * throws `ConfigurationError` rather than running with degraded
   * atomicity that would silently re-introduce the orphan-row crash
   * window the primitive exists to eliminate.
   */
  commitSchemaVersion: (
    this: void,
    params: CommitSchemaVersionParams,
  ) => Promise<SchemaVersionRow>;
  /**
   * Commit a schema version only if every requested kind is empty. The probes
   * and schema CAS run under one backend-owned write fence, preventing a
   * participating schema-managed Store write from landing between the final
   * count and commit. Raw Stores and direct backend writes are not fenced.
   */
  commitSchemaVersionIfKindsEmpty?: (
    this: void,
    params: CommitSchemaVersionParams,
    probes: readonly SchemaKindEmptinessProbe[],
  ) => Promise<CommitSchemaVersionIfKindsEmptyResult>;
  /**
   * Acquire the transaction-scoped shared fence for a schema-managed graph
   * write, then verify that the active schema still matches the Store that is
   * issuing it. Official transactional backends provide this method; custom
   * backends may omit it, in which case schema-managed Store writes fail
   * closed rather than racing a schema change. This method must be called on a
   * transaction-scoped backend. PostgreSQL locks the active schema row, so a
   * concurrent change either becomes visible at read committed or raises the
   * database's native serialization failure at stronger isolation.
   *
   * On rejection the thrown `StaleVersionError` reports the active
   * version this transaction can observe after the conflict resolves, so
   * `details.actual` names the version that won rather than the absence the
   * blocked read momentarily saw.
   */
  lockSchemaVersionForWrite?: (
    this: void,
    params: Readonly<{ graphId: string; expectedVersion: number }>,
  ) => Promise<void>;
  /**
   * PostgreSQL/PGlite transaction-scoped fast path that takes the active
   * schema `FOR SHARE` fence and the recorded graph advisory lock in one SQL
   * statement, in that order. The graph-lock CTE is reachable only through a
   * matching schema row, so a stale Store never acquires the later lock.
   *
   * Bundled PostgreSQL/PGlite transaction targets expose this member; their
   * root backends omit it so it cannot be used outside an already-open
   * transaction. SQLite/custom transaction backends omit it too. A zero-row
   * fence runs the same honest active-version diagnostic as
   * {@link GraphBackend.lockSchemaVersionForWrite} and throws its
   * `StaleVersionError`.
   */
  lockSchemaVersionAndGraphWrite?: (
    this: void,
    params: SchemaWriteFenceParams,
  ) => Promise<GraphCommandIsolation>;
  /**
   * Internal schema-lifecycle seam for features whose data preflight must
   * commit atomically with the schema CAS. The callback runs in the same
   * write transaction after the schema write fence is acquired and before the
   * version write; it receives no schema-version write methods, so callers
   * cannot bypass the CAS.
   *
   * The preflight target is a {@link SchemaCommitPreflightBackend}: the
   * transaction backend plus the schema-write DDL primitive, because a
   * transition may have to CREATE the storage its own preflight then fills —
   * and creating it outside this transaction would publish it empty whenever
   * the commit is refused.
   *
   * @internal
   */
  commitSchemaVersionWithPreflight?: (
    this: void,
    params: CommitSchemaVersionParams,
    preflight: (target: SchemaCommitPreflightBackend) => Promise<void>,
  ) => Promise<SchemaVersionRow>;
  /**
   * Atomically flips the active schema pointer to an existing version,
   * with optimistic compare-and-swap on the currently active version.
   * Used by `rollbackSchema` and any other "promote/demote existing
   * version" workflow. Throws `StaleVersionError` on CAS mismatch and
   * `MigrationError` if the target version row does not exist.
   *
   * Same transactional requirements as `commitSchemaVersion`.
   */
  setActiveVersion: (
    this: void,
    params: SetActiveVersionParams,
  ) => Promise<void>;

  /** Register an immutable materialized schema template. */
  registerGraphTemplate?: (
    this: void,
    params: Readonly<{
      templateId: string;
      schemaHash: string;
      schemaDoc: SerializedSchema;
    }>,
  ) => Promise<GraphTemplateRow>;
  /**
   * Instantiate a v1 schema from a registered template in one database
   * statement. The caller supplies the hash of the graph-id-rebound document;
   * the large schema document remains server-side.
   */
  instantiateGraphTemplate?: (
    this: void,
    params: Readonly<{
      templateId: string;
      templateSchemaHash: string;
      graphId: string;
      schemaHash: string;
    }>,
  ) => Promise<
    | Readonly<{ status: "ready"; row: SchemaVersionRow }>
    | Readonly<{ status: "refused" }>
  >;

  /**
   * Run an administrative callback while holding the same per-graph lock as
   * schema commits. The callback receives the transaction-scoped backend, so
   * its reads, DML, and DDL are committed atomically before another schema
   * writer can proceed.
   *
   * This is intentionally absent from {@link TransactionBackend}: nesting a
   * schema-write lock from an already-open transaction can deadlock. It is an
   * optional backend capability so custom backends can decline operations
   * that require a schema fence rather than silently running them unsafely.
   */
  schemaWriteTransaction?: <T>(
    this: void,
    graphId: string,
    fn: (
      tx: TransactionBackend &
        Readonly<{
          executeStatement: NonNullable<TransactionBackend["executeStatement"]>;
          tableExists: (this: void, tableName: string) => Promise<boolean>;
          executeSchemaDdl: (this: void, ddl: string) => Promise<void>;
          deleteSchemaVectorSlotContribution: (
            this: void,
            slot: VectorSlot,
          ) => Promise<void>;
        }>,
    ) => Promise<T>,
  ) => Promise<T>;

  // === Embedding Operations (optional - depends on vector capabilities) ===
  upsertEmbedding?: (
    this: void,
    params: UpsertEmbeddingParams,
  ) => Promise<void>;
  /**
   * Batched variant of `upsertEmbedding` for one vector slot. Optional —
   * callers fall back to per-row `upsertEmbedding` when unset.
   */
  upsertEmbeddingBatch?: (
    this: void,
    params: UpsertEmbeddingBatchParams,
  ) => Promise<void>;
  deleteEmbedding?: (
    this: void,
    params: DeleteEmbeddingParams,
  ) => Promise<void>;
  deleteEmbeddingBatch?: (
    this: void,
    params: Omit<DeleteEmbeddingParams, "nodeId"> &
      Readonly<{ nodeIds: readonly string[] }>,
  ) => Promise<void>;
  /**
   * KNN search over one `(nodeKind, fieldPath)` slot. Top-k is computed
   * over CURRENT nodes only — non-tombstoned AND inside their validity
   * window, matching a `current` read — so index drift (embedding rows
   * whose node was deleted or expired outside the store pipeline) can
   * neither surface nor crowd current rows out of the top-k. Custom
   * backends implementing this member must honor the same contract. Exact on pgvector >= 0.8 (HNSW via
   * `hnsw.iterative_scan = strict_order`; IVFFlat via
   * `ivfflat.iterative_scan = relaxed_order` plus a re-sort of the
   * bounded set; probe-bounded below 0.8) and sqlite-vec;
   * libSQL's DiskANN over-fetches 4x and post-filters (recall bounded by
   * that headroom).
   */
  vectorSearch?: (
    this: void,
    params: VectorSearchParams,
  ) => Promise<readonly VectorSearchResult[]>;
  createVectorIndex?: (
    this: void,
    params: CreateVectorIndexParams,
  ) => Promise<void>;
  dropVectorIndex?: (
    this: void,
    params: DropVectorIndexParams,
  ) => Promise<void>;
  /**
   * Single-statement hybrid search: both sources, RRF fusion, liveness
   * join, and node hydration composed into ONE statement — replacing the
   * facade's two search round trips plus id-hydration fetch. Optional;
   * the store falls back to the multi-statement path when unset (custom
   * backends, engines without window functions). Same liveness contract
   * as `vectorSearch` / `fulltextSearch`.
   */
  hybridSearch?: (
    this: void,
    params: HybridSearchParams,
  ) => Promise<readonly HybridSearchRow[]>;

  // === Fulltext Operations (optional - depends on fulltext capabilities) ===
  upsertFulltext?: (this: void, params: UpsertFulltextParams) => Promise<void>;
  deleteFulltext?: (this: void, params: DeleteFulltextParams) => Promise<void>;
  /**
   * Batched variant of `upsertFulltext`. Optional — callers fall back to
   * per-row `upsertFulltext` when unset.
   */
  upsertFulltextBatch?: (
    this: void,
    params: UpsertFulltextBatchParams,
  ) => Promise<void>;
  /**
   * Batched variant of `deleteFulltext`. Optional — callers fall back to
   * per-row `deleteFulltext` when unset.
   */
  deleteFulltextBatch?: (
    this: void,
    params: DeleteFulltextBatchParams,
  ) => Promise<void>;
  /**
   * Ranked fulltext search over one node kind. Like `vectorSearch`, top-k
   * is computed over CURRENT nodes only — non-tombstoned AND inside their
   * validity window (exact on both engines — plain WHERE, no top-k table
   * function involved). Custom backends implementing this member must
   * honor the same contract.
   */
  fulltextSearch?: (
    this: void,
    params: FulltextSearchParams,
  ) => Promise<readonly FulltextSearchResult[]>;

  // === Index Materialization (used by store.materializeIndexes) ===
  /**
   * Idempotently ensure ONLY the `typegraph_index_materializations`
   * table exists — separate from `bootstrapTables` so that
   * `materializeIndexes` doesn't pull in the full base-table DDL set
   * just to access the status table.
   *
   * Why focused: `bootstrapTables` issues 20+ `CREATE TABLE / CREATE
   * INDEX IF NOT EXISTS` statements covering every base table. Two
   * concurrent calls (e.g. two replicas of the same `schema_doc` both
   * starting up and calling `materializeIndexes`) race on
   * Postgres SHARE locks and DEADLOCK. Restricting the ensure-step to
   * the single status table eliminates the cross-table race entirely
   * — concurrent `CREATE TABLE IF NOT EXISTS` for one specific table
   * is well-behaved on Postgres.
   */
  ensureIndexMaterializationsTable?: (this: void) => Promise<void>;

  /**
   * Install the PostgreSQL `pg_trgm` extension under a database-global
   * concurrency fence. Relational trigram index materialization calls this
   * before emitting its index DDL; non-PostgreSQL backends omit it.
   *
   * @deprecated The `pg_trgm`-only spelling of {@link GraphBackend.ensureExtension},
   *   which says the same thing for every extension the library installs. It is
   *   retained — and still consulted, after `ensureExtension` — so a backend
   *   written against 0.47 keeps its fence; the bundled PostgreSQL backend
   *   implements it by delegating. Implement `ensureExtension` in new backends.
   */
  ensureTrigramExtension?: (this: void) => Promise<void>;

  /**
   * Idempotently ensure ONLY the `typegraph_revision_origins` table exists.
   *
   * Revision-tracked stores use this per-graph, durable random origin together
   * with the recorded clock to prevent two independent stores with coincident
   * timestamps from sharing a merge base anchor. It is focused rather than
   * using `bootstrapTables` so existing deployments can adopt revision anchors
   * without replaying all base-table DDL during a merge read.
   */
  ensureRevisionOriginsTable?: (this: void) => Promise<void>;

  /**
   * Idempotently add the durable edge-match identity columns, pair constraint,
   * and unique index to the configured edge relation.
   *
   * Every edge write names these nullable columns, even when its graph does
   * not declare `matchIdentity`. Privileged schema preparation therefore calls
   * this focused hook on every open so pre-provisioned base tables adopt newer
   * physical storage without replaying the complete base-table DDL set.
   * Ordinary runtime store construction never performs DDL.
   *
   * @internal
   */
  ensureEdgeMatchIdentityStorage?: (this: void) => Promise<void>;

  /**
   * Idempotently ensure ONLY the three Operational Identity relations exist —
   * the current-assertions table, the recorded-time assertions table, and the
   * derived closure and separation tables — with their indexes and CHECK
   * constraints (CREATE TABLE / CREATE INDEX IF NOT EXISTS).
   *
   * First enablement of identity on an existing populated deployment attaches
   * via `createStore` / `createSqliteBackend` / `createPostgresBackend`, none
   * of which run DDL, so the enablement preflight would otherwise
   * SELECT/DELETE/INSERT tables that do not exist yet. The store calls this
   * before the enablement locks and closure rebuild. Focused rather than
   * `bootstrapTables` for the same concurrency rationale as
   * {@link ensureRevisionOriginsTable}. Stores call it before opening the
   * schema-commit transaction so DDL does not re-enter its per-graph lock.
   * Returns the logical names that were absent before this call. Callers set
   * `provisionMissing` only for safe first enablement; on an already-enabled
   * graph, missing ledger tables are deliberately left absent so a failed open
   * cannot make its next retry silently accept empty replacement storage.
   *
   * @internal
   */
  ensureIdentityTables?: (
    this: void,
    tableNames: IdentityTableNames,
    options: Readonly<{ provisionMissing: boolean }>,
  ) => Promise<readonly string[]>;

  /**
   * The idempotent CREATE statements for exactly the identity relations —
   * the same DDL {@link ensureIdentityTables} issues, handed back as data
   * instead of executed.
   *
   * Pure and synchronous: it executes nothing and probes no catalog, so it is
   * safe to call from inside an already-open transaction, where invoking a
   * top-level backend method would re-enter the backend's serialized statement
   * queue and deadlock. That is the whole point. Provisioning a missing DERIVED
   * relation (closure, separation) on an already-enabled graph has to CREATE it
   * and FILL it in one transaction, because a created-but-empty derived
   * relation is readable and answers every question with "nothing" — for the
   * separation relation, "nothing" means "not separated", which is exactly the
   * answer that lets a contradictory merge commit.
   *
   * A backend that omits this cannot offer that atomicity. Callers do not fall
   * back: when a fill is owed, the upgrade is REFUSED with the typed
   * `IDENTITY_UPGRADE_REQUIRES_ATOMIC_DDL` error naming this port — creating
   * and filling back-to-back would publish the readable-empty state the
   * paragraph above forbids.
   *
   * @internal
   */
  identityTableDdl?: (
    this: void,
    tableNames: IdentityTableNames,
  ) => readonly string[];

  /**
   * The provisioning DDL for the three recorded relations under the given
   * physical names, keyed by logical relation. Used only by the offline legacy
   * preview-schema migration, which builds temp tables, copies, and swaps —
   * so it is called TWICE per migration, once per name set, and the migration
   * (not the backend) composes the temp `createTable` with the final `indexes`.
   *
   * A backend that omits this cannot be migrated FROM the timestamp-only
   * preview schema — a schema only the bundled Drizzle backends ever created.
   * `migrateLegacyRecordedTime` REFUSES with `UnsupportedBackendCapabilityError`
   * naming this port rather than emitting DDL it cannot author.
   *
   * @internal
   */
  recordedTableDdl?: (
    this: void,
    tableNames: RecordedTableNames,
  ) => Readonly<Record<keyof RecordedTableNames, RecordedRelationDdl>>;

  /**
   * Look up a recorded materialization for a declared index by its
   * physical SQL index name. Returns `undefined` if no row exists.
   */
  getIndexMaterialization?: (
    this: void,
    indexName: string,
  ) => Promise<IndexMaterializationRow | undefined>;
  /**
   * Bulk variant of `getIndexMaterialization`: load every recorded
   * materialization whose `indexName` (status key) is in `statusKeys`,
   * in a single round-trip. Returned rows are unordered — callers index
   * by `indexName`. Optional; consumers fall back to per-key
   * `getIndexMaterialization` when unset.
   */
  getIndexMaterializations?: (
    this: void,
    statusKeys: readonly string[],
  ) => Promise<readonly IndexMaterializationRow[]>;
  /**
   * Upsert a materialization attempt — success or failure. Failure rows
   * preserve any prior `materializedAt` so the historical successful
   * timestamp survives across error windows.
   */
  recordIndexMaterialization?: (
    this: void,
    params: RecordIndexMaterializationParams,
  ) => Promise<void>;
  /**
   * Atomically claims the build of one index across every materializer
   * (process- and pool-safe: the status row's own atomicity is the mutex).
   * Returns true when this caller now holds the claim. Backends whose
   * index builds cannot deadlock across callers (SQLite: engine-level
   * write serialization, no CONCURRENTLY) omit it; `materializeIndexes`
   * then builds without a claim, exactly as before.
   */
  claimIndexMaterialization?: (
    this: void,
    params: ClaimIndexMaterializationParams,
  ) => Promise<boolean>;
  /** Releases a claim taken by `claimIndexMaterialization` (token-guarded). */
  releaseIndexMaterializationClaim?: (
    this: void,
    params: ReleaseIndexMaterializationClaimParams,
  ) => Promise<void>;

  /**
   * Physical-schema introspection: table/index presence, PostgreSQL's
   * invalid-index leftover state, and normalized column types. Present on
   * both bundled Drizzle backends; a custom backend that omits it loses the
   * store paths that consult it directly: index materialization
   * (`store.materializeIndexes()` refuses only once its empty-candidate
   * short circuit and the status-table ensure step have already run;
   * `store.materializeSystemIndexes()`, which has no candidate short
   * circuit, refuses only once that same status-table ensure step has
   * run), the recorded-time schema check, and the recorded-time
   * migration's column read.
   */
  catalog?: BackendCatalogProbes | undefined;

  /**
   * The engine's whole-database revision and the per-graph change delta
   * since an earlier one. Present only when a backend's engine declares it
   * (`EngineProvisioning.lineage`) — absent by default on a custom backend
   * that supplies none, and absent on both bundled Drizzle profiles
   * regardless of `history`. A history-enabled store never populates this
   * member itself: it always resolves its lineage from its own recorded
   * relations instead (`resolveLineage` in
   * `store/recorded-capture/lineage.ts`), never from this member. Every
   * consumer falls back to a full comparison when this is absent — see
   * `requireLineage` in `backend/capabilities/lineage.ts`.
   */
  lineage?: LineageMembers | undefined;

  /**
   * The engine's own recorded (system-time) read source and revision clock.
   * Present only when a backend's engine declares it
   * (`EngineProvisioning.recordedTime`) — absent by default on a custom
   * backend that supplies none, and absent on both bundled Drizzle profiles,
   * which always allocate TypeGraph's own recorded clock and relations
   * instead. Declaring this member is what makes a backend engine-native for
   * recorded time — see `resolveRecordedTimeOwnership` in
   * `backend/capabilities/recorded-time-ownership.ts`, the one reader of
   * that distinction. A backend that supplies `recordedTime` must also
   * supply `lineage`: engine-native history keeps no recorded relations of
   * its own to derive a change delta from, so `createSqlBackend` refuses a
   * profile that declares one without the other.
   */
  recordedTime?: EngineRecordedTimeMembers | undefined;

  // === Contribution Materialization (#135 — durable strategy-owned
  // storage marker, sibling of the index status table) ===

  /**
   * Idempotently ensure ONLY the
   * `typegraph_contribution_materializations` table exists. Same
   * focused-bootstrap rationale as `ensureIndexMaterializationsTable`:
   * a single `CREATE TABLE IF NOT EXISTS` is concurrency-safe under
   * replica startup, where the full `bootstrapTables` set risks a
   * Postgres SHARE-lock deadlock.
   */
  ensureContributionMaterializationsTable?: (this: void) => Promise<void>;

  /**
   * Look up the durable materialization marker for one strategy-owned
   * contribution identity. Returns `undefined` when no row exists
   * ("never initialized").
   */
  getContributionMaterialization?: (
    this: void,
    identity: ContributionMaterializationIdentity,
  ) => Promise<ContributionMaterializationRow | undefined>;

  /**
   * Upsert a contribution-materialization attempt — success or failure.
   * Failure rows preserve any prior `materializedAt` via COALESCE so a
   * later failed re-attempt doesn't erase the historical success.
   */
  recordContributionMaterialization?: (
    this: void,
    params: RecordContributionMaterializationParams,
  ) => Promise<void>;

  /**
   * Resolve (once per backend instance, cached) and assert the durable
   * materialization markers for every `runtimeEnsure` contribution this
   * backend's strategy declares, for `graphId`. Throws
   * `StoreNotInitializedError` when a marker is missing, stale
   * (signature drift), or recorded a failed last attempt.
   *
   * This is the single read-side gate the fulltext hot-path wrappers
   * and `store.transaction()` consult. It performs ZERO DDL and ZERO
   * marker writes — initialization is the exclusive job of the async
   * boot path (`createStoreWithSchema` → `ensureRuntimeContributions`).
   */
  assertRuntimeContributionsInitialized?: (
    this: void,
    graphId: string,
  ) => Promise<void>;

  // === Kind Removal Status ===

  /**
   * Bootstraps the per-deployment `typegraph_kind_removals` table so
   * `store.evolve()` can check pending removals and the removal verbs can
   * persist and materialize removal status. Mirrors the focused-bootstrap
   * rationale documented on `ensureIndexMaterializationsTable` — the full
   * `bootstrapTables` touches every base table and risks Postgres SHARE-lock
   * deadlock under concurrent replica startup.
   */
  ensureKindRemovalsTable?: (this: void) => Promise<void>;

  /**
   * List graph-extension kind removals whose data-cleanup pass has not yet
   * succeeded for this `graphId`. Returns rows with
   * `removedAt: undefined`. Order is unspecified; callers materialize
   * one-at-a-time and don't depend on it.
   */
  getPendingKindRemovals?: (
    this: void,
    graphId: string,
  ) => Promise<readonly KindRemovalRow[]>;

  /**
   * List ALL kind-removal rows for a `graphId` — pending and completed.
   * Used by `materializeRemovals()` reconciliation to detect rows that
   * are missing entirely (the `removeKinds()` crash window) versus
   * already completed. Without this distinction the reconciler would
   * have to upsert every expected historical removal on every call,
   * churning `last_attempted_at` on rows that long since succeeded.
   * Order is unspecified.
   */
  getAllKindRemovals?: (
    this: void,
    graphId: string,
  ) => Promise<readonly KindRemovalRow[]>;

  /**
   * Upsert a kind-removal status row. `removedAt: undefined` records
   * the pending state at schema-commit time; `removedAt: <iso>`
   * marks the data cleanup successful. The COALESCE rule on `removedAt`
   * mirrors `recordIndexMaterialization` so a later failure doesn't
   * clobber the historical successful timestamp from another replica.
   */
  recordKindRemoval?: (
    this: void,
    params: RecordKindRemovalParams,
  ) => Promise<void>;

  // === Reconciliation Watermark ===

  /**
   * Bootstraps the per-deployment `typegraph_reconciliation_markers`
   * table so `materializeRemovals()` can persist reconciliation
   * progress. Same focused-bootstrap rationale as the other status
   * tables — full `bootstrapTables` risks Postgres SHARE-lock
   * deadlock under concurrent replica startup.
   */
  ensureReconciliationMarkersTable?: (this: void) => Promise<void>;

  // === Table Contributions (#129) ===

  /**
   * Materializes every contribution flagged `runtimeEnsure` — the
   * strategy-owned runtime tables (fulltext today) that drizzle-kit-
   * managed setups don't create. Called once after a successful schema
   * load. Deliberately scoped: base/drizzle-visible tables are
   * `runtimeEnsure: false`, so this does not regress startup into
   * broad DDL/probing across every table.
   *
   * The canonical durable-marker writer (#135): for each runtime
   * contribution it short-circuits when the marker already records a
   * matching signature, otherwise runs the idempotent `createDdl` and
   * records the marker (success or failure) keyed by `graphId`.
   */
  ensureRuntimeContributions?: (this: void, graphId: string) => Promise<void>;

  /**
   * Privileged materializer for one embedding `(kind, field)` slot's
   * `ownedTables` contribution(s): creates the per-field vector table and
   * records its durable marker, idempotently. The vector counterpart of
   * `ensureRuntimeContributions` — vectors are per-`(kind, field)` and
   * graph-derived, so the store enumerates slots (via
   * `resolveEmbeddingFields`) and uses the batch counterpart at boot under
   * the privileged role. Pass `{ force: true }` to overwrite the marker
   * at the current signature, bypassing the drift-guard — the sanctioned
   * path `store.reembedVectorField()` uses after recreating storage at a
   * new dimension. Pass `{ onDrift: "skip" }` to leave an
   * already-provisioned slot whose shape has since changed untouched
   * (warn + no-op) instead of refusing — the boot/evolve path, so a
   * declared dimension change never blocks startup before the operator
   * can run `reembedVectorField`. Present only on backends wired with a
   * vector strategy.
   */
  ensureVectorSlotContribution?: (
    this: void,
    slot: VectorSlot,
    options?: Readonly<{ force?: boolean; onDrift?: "throw" | "skip" }>,
  ) => Promise<void>;

  /**
   * Batch form of `ensureVectorSlotContribution`, used by privileged boot to
   * resolve every slot's durable markers with one graph-scoped query. The
   * singular method remains available for re-embedding and compatibility.
   */
  ensureVectorSlotContributions?: (
    this: void,
    slots: readonly VectorSlot[],
    options?: Readonly<{ force?: boolean; onDrift?: "throw" | "skip" }>,
  ) => Promise<void>;

  /**
   * SELECT-only gate for one embedding `(kind, field)` slot: asserts the
   * durable marker(s) for the slot's `ownedTables` contribution(s),
   * cached per backend instance. Throws `StoreNotInitializedError` when
   * the slot is missing, stale (signature drift), or recorded a failed
   * last attempt. The vector counterpart of
   * `assertRuntimeContributionsInitialized`, consulted by the verified
   * runtime attach. Performs ZERO DDL and ZERO writes. Present only on
   * backends wired with a vector strategy.
   */
  assertVectorSlotInitialized?: (this: void, slot: VectorSlot) => Promise<void>;

  /**
   * Batch form of `assertVectorSlotInitialized`, used by verified attach to
   * resolve every slot's durable markers with one graph-scoped query.
   */
  assertVectorSlotsInitialized?: (
    this: void,
    slots: readonly VectorSlot[],
  ) => Promise<void>;

  /**
   * Forget one embedding `(kind, field)` slot's durable contribution
   * marker(s). Called after the slot's per-field table is dropped
   * (vector-field reclaim) so a later `ensureVectorSlotContribution`
   * re-creates the table instead of trusting an orphaned "initialized"
   * marker. Does NOT drop the table itself (the caller already did).
   * Present only on backends wired with a vector strategy.
   */
  deleteVectorSlotContribution?: (
    this: void,
    slot: VectorSlot,
  ) => Promise<void>;

  /**
   * Diagnostic: compare each contribution currently expected for `graphId`
   * against its durable marker and the physical catalog.
   *
   * Covers the strategy-owned `runtimeEnsure` contributions (fulltext)
   * plus the `ownedTables` contribution(s) of each supplied vector slot.
   * Returns one {@link ContributionDiagnostic} per detected unusable
   * contribution. A recorded failed materialization is unusable even when
   * marker and catalog agree that no table was produced. A contribution with
   * neither a marker nor a table is treated as never attempted and omitted;
   * marker rows outside the current declaration set are not audited. An empty
   * array therefore is not proof that storage was initialized.
   *
   * Deliberately NOT part of the open path. `ensureRuntimeContributions`
   * and `assertRuntimeContributionsInitialized` short-circuit on a
   * per-instance signature cache and then on the marker row alone, which
   * is the right default for a hot path but leaves a database whose
   * contribution tables were dropped out of band opening clean and
   * failing at the first dependent read or write. This method is the explicit,
   * operator-invoked catalog probe that fills that gap: it issues one uncached
   * existence query per distinct physical table and performs ZERO DDL
   * and ZERO writes, so it is safe under a least-privilege runtime role.
   *
   * Present only on backends that can probe their own catalog.
   */
  verifyContributions?: (
    this: void,
    graphId: string,
    vectorSlots: readonly VectorSlot[],
  ) => Promise<readonly ContributionDiagnostic[]>;

  /**
   * Re-audit current contribution declarations and non-destructively repair
   * `missing-marker` and `failed-materialization` findings. The backend must
   * resolve every target itself; it must not accept caller-provided physical
   * identities or DDL. `stale` and `orphaned-marker` findings are returned as
   * `requires-rebuild` because repairing either can require destructive data
   * reconstruction.
   *
   * Present only on backends that can both probe their catalog and run the
   * strategy-owned contribution DDL.
   */
  repairContributions?: (
    this: void,
    graphId: string,
    vectorSlots: readonly VectorSlot[],
  ) => Promise<ContributionRepairResult>;

  /**
   * Read-only readiness probe: the same marker-versus-catalog audit
   * `verifyContributions` performs, projected onto one entry per search
   * projection. Shares that method's detection logic exactly — a second
   * implementation would be free to disagree with the one the hot-path
   * gate actually consults, and a health check that disagrees with the
   * gate is worse than none.
   *
   * Writes nothing: no DDL, no marker writes, no effect on the
   * per-instance caches the hot path relies on. Safe on a read path, on
   * a replica, and under a least-privilege role.
   *
   * Present only on backends that can probe their catalog; a backend
   * that provisions contributions without this method declares the gap
   * as `capabilities.contributions.probe === false`.
   */
  probeContributions?: (
    this: void,
    graphId: string,
    vectorSlots: readonly VectorSlot[],
  ) => Promise<readonly ContributionProbeEntry[]>;

  /**
   * Destructively rebuild one search projection for one graph: clear that
   * graph's rows from the projection's storage, ensure the storage matches
   * the current `createDdl`, reconstruct the graph's content, and stamp the
   * durable marker at the current signature.
   *
   * This is the repair `repairContributions` deliberately refuses to
   * perform, and it must never be reachable from it. A `stale`
   * contribution's table exists at the *old* physical shape, so the
   * idempotent `CREATE ... IF NOT EXISTS` in the ordinary ensure path
   * no-ops; re-stamping the marker there would leave it blessing a table
   * whose shape is wrong, which is exactly what the drift guard exists
   * to prevent. Only a drop makes the recreate meaningful, and destroying
   * content is a decision the caller states rather than one a flag named
   * `force` implies.
   *
   * The fulltext projection's storage is one physical table shared by every
   * graph in the database, while this call is fenced per graph.
   * Implementations must therefore scope the teardown to `graphId` and may
   * drop the storage only when doing so destroys no other graph's rows;
   * when the recorded shape is stale and the drop that would repair it is
   * not available, they refuse (`shared-storage-in-use`) rather than
   * re-stamp a shape nothing verified.
   *
   * Runs the whole sequence inside one transaction under the same
   * per-graph fence as a schema commit, so an interrupted rebuild leaves
   * the contribution exactly as it was rather than attested-but-empty.
   *
   * `repopulate` receives that transaction and reconstructs content from
   * rows TypeGraph already stores. The inversion exists because deciding
   * *what* content a node contributes needs the schema registry, which
   * is a Store-layer concern the backend must not reach into.
   *
   * @param scope which projection to rebuild. Implementations must
   *   refuse `"vector"`: embeddings live only in the storage this would
   *   drop, so there is nothing to reconstruct them from.
   */
  rebuildContribution?: (
    this: void,
    graphId: string,
    scope: ContributionRebuildScope,
    repopulate: (
      target: TransactionBackend,
    ) => Promise<ContributionRepopulationStats>,
  ) => Promise<ContributionRebuildResult>;

  /**
   * Bootstraps the fulltext storage table the active `FulltextStrategy`
   * owns. Same focused-bootstrap rationale as the other `ensure*Table`
   * methods: idempotent and concurrency-safe under replica startup.
   *
   * Superseded by `ensureRuntimeContributions()` (#129); retained as
   * a thin back-compat wrapper for backends/callers predating #129. Not
   * machine-`@deprecated` because the manager still calls it as the
   * pre-#129 fallback. #135 removed the remaining hot-path callers and
   * routed this through the durable-marker writer.
   */
  ensureFulltextTable?: (this: void, graphId: string) => Promise<void>;

  /**
   * Read the high-water mark schema version for which
   * `materializeRemovals` reconciliation has already verified history
   * for `graphId`. Returns `undefined` when no marker has been
   * recorded yet. Used to skip already-checked transitions in the
   * recovery walk.
   */
  getReconciliationMarker?: (
    this: void,
    graphId: string,
  ) => Promise<number | undefined>;

  /**
   * Persist the reconciliation high-water mark for `graphId`. Called
   * after `materializeRemovals` completes a clean walk; subsequent
   * calls walk only versions newer than this marker. Idempotent
   * upsert by `graphId`.
   */
  setReconciliationMarker?: (
    this: void,
    graphId: string,
    version: number,
  ) => Promise<void>;

  // === Graph Lifecycle ===
  /**
   * Adopts deployment-wide base relations to the physical schema required by
   * this library version and stamps the singleton installation marker.
   * Privileged schema-management entry points call this; ordinary raw Store
   * construction never does.
   *
   * @internal
   */
  adoptBaseSchema?: (this: void) => Promise<void>;

  /**
   * SELECT-only sibling of {@link GraphBackend.adoptBaseSchema}. Throws a
   * typed base-schema migration error when privileged adoption has not run.
   * Least-privilege verified/template entry points call this before DML.
   *
   * @internal
   */
  assertBaseSchemaCurrent?: (this: void) => Promise<void>;

  /**
   * Hard-deletes all data for a graph (nodes, edges, uniques, embeddings, schema versions).
   * Intended for import-replacement workflows. No hooks, no per-row logic.
   */
  clearGraph: (this: void, graphId: string) => Promise<void>;

  /**
   * Creates the base TypeGraph tables if they don't already exist.
   *
   * Called automatically by `createStoreWithSchema()` when a fresh database
   * is detected. Users who manage DDL themselves via `createStore()` never
   * hit this path.
   */
  bootstrapTables?: (this: void) => Promise<void>;

  /**
   * Refreshes the backend's query-planner statistics.
   *
   * Call this once after a large initial import or bulk backfill. Without
   * up-to-date statistics, the planner can pick suboptimal execution plans
   * — on PostgreSQL this is the difference between a 0.5ms and a 5ms
   * forward traversal; on SQLite it's the difference between 0.9ms and
   * 23ms fulltext search. Autovacuum / background statistics collection
   * will catch up eventually, but calling this explicitly after a bulk
   * load gives you correct latencies immediately.
   *
   * Implementations:
   * - SQLite runs `ANALYZE`, which populates `sqlite_stat1`
   * - PostgreSQL runs `ANALYZE` on the TypeGraph-managed tables
   *
   * Safe to call at any time; costs a few tens of milliseconds on the
   * sizes this library is designed for.
   */
  refreshStatistics: (this: void) => Promise<void>;

  /**
   * Runs an intentionally trusted, all-or-nothing initial import.
   *
   * Optional because only backends that can pin one transaction and use a
   * native high-throughput write path expose it. Implementations must reject
   * a database whose TypeGraph node or edge tables already contain rows. They
   * may temporarily remove rebuildable secondary indexes, but must restore
   * them before committing. Any callback, insert, or rebuild failure rolls the
   * entire operation back.
   *
   * This is a top-level-only lifecycle operation and is deliberately absent
   * from {@link TransactionBackend}. When `schemaWrite` is supplied, the
   * import acquires and validates that Store version's managed-write fence
   * before checking emptiness or writing. Omitting it retains the raw,
   * unversioned import behavior.
   */
  trustedImport?: <T>(
    this: void,
    fn: (session: TrustedImportSession) => Promise<T>,
    options?: Readonly<{
      schemaWrite?: Readonly<{ graphId: string; expectedVersion: number }>;
    }>,
  ) => Promise<T>;

  // === Query Execution ===
  execute: <T>(this: void, query: CompiledRowsSql) => Promise<readonly T[]>;

  /**
   * Execute a non-row-returning SQL statement bound to this backend or
   * transaction. Optional because custom backends may only expose the public
   * row-returning query path; features that need statement execution must
   * capability-check and fail loudly.
   */
  executeStatement?: (this: void, query: CompiledStatementSql) => Promise<void>;

  /**
   * Execute an internally compiled statement against connection-local
   * temporary state. History wrappers preserve this path because it cannot
   * mutate graph or history tables; callers cannot construct its branded
   * input through the public API.
   */
  executeTemporaryStatement?: (
    this: void,
    query: CompiledTemporaryStatementSql,
  ) => Promise<void>;

  /** Execute pre-compiled SQL text with bound parameters. Available on sync SQLite and pg backends. */
  executeRaw?: <T>(
    this: void,
    sqlText: string,
    params: readonly unknown[],
  ) => Promise<readonly T[]>;

  /** Compile a TypeGraph `SqlFragment` to `{ sql, params }` without executing. */
  compileSql?: (
    this: void,
    query: SqlFragment,
  ) => Readonly<{ sql: string; params: readonly unknown[] }>;

  /**
   * Execute a DDL statement that returns no rows (CREATE INDEX,
   * CREATE TABLE, ALTER TABLE, etc.). Separate from `executeRaw`
   * because some drivers (better-sqlite3) require `.run()` for DDL
   * and `.all()` for queries — the ambiguity can't be resolved by
   * inspecting the SQL string portably.
   *
   * Postgres path can use this for `CREATE INDEX CONCURRENTLY`, which
   * cannot run inside a transaction. Implementations must execute the
   * statement outside `transaction(...)`.
   */
  executeDdl?: (this: void, ddl: string) => Promise<void>;

  /**
   * Installs a database-global extension idempotently, tolerating the
   * concurrent-install race.
   *
   * `CREATE EXTENSION IF NOT EXISTS` is not a concurrency primitive on
   * PostgreSQL: the existence check cannot see another session's uncommitted
   * `pg_extension` row, so the loser of a race waits for the winner and is
   * then handed SQLSTATE 23505 instead of the harmless "already exists"
   * notice (#446). A backend implementing this member owns that retry, and
   * may additionally serialize same-extension installers behind a fence of its
   * own (the bundled PostgreSQL backend takes a transaction advisory lock
   * keyed on the extension, #475).
   *
   * This is the one member the library asks for an extension install; it
   * supersedes {@link GraphBackend.ensureTrigramExtension}, which said the same
   * thing for one extension.
   *
   * Like `executeDdl`, implementations MUST run the statement at the
   * top-level backend, never inside `transaction(...)`: the 23505 aborts the
   * enclosing transaction, so a retry issued inside one would only collect
   * `25P02` on the way out. That is why this member is absent from
   * {@link TransactionBackend}.
   */
  ensureExtension?: (this: void, name: DatabaseExtensionName) => Promise<void>;

  // === Transaction ===
  /** Runs TypeGraph operations inside a backend-owned transaction. */
  transaction: <T>(
    this: void,
    fn: (tx: TransactionBackend) => Promise<T>,
    options?: TransactionOptions,
  ) => Promise<T>;

  // === Lifecycle ===
  close: (this: void) => Promise<void>;
}> &
  DurableEdgeBatchMembers;

/** Policy for provisioning physical schema inside a caller-owned transaction. */
export type SchemaProvisioning = "dml-only" | "transactional";

/**
 * Adapter-native transaction interoperability layered on top of the portable
 * TypeGraph backend. Only adapter entrypoints expose this capability.
 */
export type AdapterBackend<TNativeTransaction> = GraphBackend &
  Readonly<{
    /** Whether caller-owned schema transactions may provision physical storage. */
    schemaProvisioning: SchemaProvisioning;
    /**
     * Runs TypeGraph operations and exposes the exact adapter-native handle
     * bound to the same transaction.
     */
    transactionWithNative: <T>(
      this: void,
      fn: (
        tx: TransactionBackend,
        nativeTransaction: TNativeTransaction,
      ) => Promise<T>,
      options?: TransactionOptions,
    ) => Promise<T>;
    /** Adopts a caller-owned, already-open adapter transaction. */
    adoptTransaction: (
      this: void,
      externalTransaction: TNativeTransaction,
    ) => TransactionBackend;
    /**
     * Adopt a caller-owned native transaction for a schema change. The adapter
     * proves the transaction is active and acquires its schema-write fence on
     * that literal session before returning the privileged CAS target. It
     * never opens, commits, retries, or rolls back the caller's transaction.
     * Optional because drivers without active-session evidence must refuse.
     */
    adoptSchemaWriteTransaction?: (
      this: void,
      externalTransaction: TNativeTransaction,
      graphId: string,
      options: Readonly<{ waitBudgetMs: number }>,
    ) => Promise<AdoptedSchemaWriteTransaction>;
  }>;

/**
 * The schema-write facet available only after adoption earned its fence.
 * The caller owns the native transaction and remains responsible for its commit
 * or rollback.
 */
export type AdoptedSchemaWriteTransaction = Readonly<{
  backend: SchemaWriteTransactionBackend &
    Readonly<{
      commitSchemaVersion: GraphBackend["commitSchemaVersion"];
      ensureVectorSlotContributions?: (
        this: void,
        slots: readonly VectorSlot[],
        options?: Readonly<{ onDrift?: "throw" | "skip" }>,
      ) => Promise<void>;
    }>;
  activeSchema: SchemaVersionRow | undefined;
}>;

export type BackendIdentity = Pick<
  GraphBackend,
  | "dialect"
  | "capabilities"
  | "tableNames"
  | "fulltextStrategy"
  | "vectorStrategy"
  | "fenceSql"
>;

export type NodeEntityReadBackend = Pick<
  GraphBackend,
  "getNode" | "getNodes" | "findNodesByKind" | "countNodesByKind"
>;

export type NodeEntityWriteBackend = Pick<
  GraphBackend,
  | "insertNode"
  | "insertNodeIfAbsent"
  | "insertNodeIfAbsentWithSchemaFence"
  | "insertNodeWithSchemaFence"
  | "commands"
  | "insertNodeNoReturn"
  | "insertNodesBatch"
  | "insertNodesBatchReturning"
  | "updateNode"
  | "upsertHeterogeneousNodes"
  | "updateResolvedNodesBatch"
  | "compareAndSetNode"
  | "updateNodeSet"
  | "deleteNode"
  | "hardDeleteNode"
>;

export type EdgeEntityReadBackend = Pick<
  GraphBackend,
  | "getEdge"
  | "getEdges"
  | "countEdgesFrom"
  | "edgeExistsBetween"
  | "findEdgesConnectedTo"
  | "findEdgesByKind"
  | "findEdgesByEndpointSet"
  | "findEdgesByHeterogeneousEndpointSet"
  | "countEdgesByKind"
>;

export type EdgeEntityWriteBackend = Pick<
  GraphBackend,
  | "insertEdge"
  | "commands"
  | "insertEdgeNoReturn"
  | "insertEdgesBatch"
  | "insertEdgesBatchReturning"
  | "insertEdgesDurableBatchReturning"
  | "updateEdge"
  | "deleteEdge"
  | "deleteEdgesBatch"
  | "hardDeleteEdge"
  | "hardDeleteEdgesBatch"
>;

export type GraphEntityReadBackend = NodeEntityReadBackend &
  EdgeEntityReadBackend;

export type GraphEntityWriteBackend = NodeEntityWriteBackend &
  EdgeEntityWriteBackend;

export type UniqueConstraintBackend = Pick<
  GraphBackend,
  | "insertUnique"
  | "insertUniqueBatch"
  | "deleteUnique"
  | "hardDeleteUniquesByNodeIds"
  | "hardDeleteUniquesByConcreteKind"
  | "checkUnique"
  | "checkUniqueBatch"
>;

export type SchemaReadBackend = Pick<
  GraphBackend,
  "getActiveSchema" | "getSchemaVersion"
>;

export type SchemaCommitBackend = Pick<
  GraphBackend,
  "commitSchemaVersion" | "commitSchemaVersionIfKindsEmpty" | "setActiveVersion"
>;

export type SchemaWriteFenceBackend = Pick<
  GraphBackend,
  "lockSchemaVersionForWrite" | "lockSchemaVersionAndGraphWrite"
>;

export type VectorOperationBackend = Pick<
  GraphBackend,
  | "upsertEmbedding"
  | "upsertEmbeddingBatch"
  | "deleteEmbedding"
  | "deleteEmbeddingBatch"
  | "vectorSearch"
  | "createVectorIndex"
  | "dropVectorIndex"
>;

export type FulltextOperationBackend = Pick<
  GraphBackend,
  | "upsertFulltext"
  | "deleteFulltext"
  | "upsertFulltextBatch"
  | "deleteFulltextBatch"
  | "fulltextSearch"
>;

export type IndexMaterializationBackend = Pick<
  GraphBackend,
  | "ensureIndexMaterializationsTable"
  | "getIndexMaterialization"
  | "getIndexMaterializations"
  | "recordIndexMaterialization"
>;

/** The optional catalog-introspection surface. See {@link BackendCatalogProbes}. */
export type CatalogBackend = Pick<GraphBackend, "catalog">;

/** The optional engine-lineage surface. See {@link LineageMembers}. */
export type LineageBackend = Pick<GraphBackend, "lineage">;

/** The optional engine-native recorded-time surface. See {@link EngineRecordedTimeMembers}. */
export type RecordedTimeBackend = Pick<GraphBackend, "recordedTime">;

export type ContributionMaterializationBackend = Pick<
  GraphBackend,
  | "ensureContributionMaterializationsTable"
  | "getContributionMaterialization"
  | "recordContributionMaterialization"
  | "assertRuntimeContributionsInitialized"
  | "ensureRuntimeContributions"
  | "ensureFulltextTable"
>;

export type RemovalMaterializationBackend = Pick<
  GraphBackend,
  | "ensureKindRemovalsTable"
  | "getPendingKindRemovals"
  | "getAllKindRemovals"
  | "recordKindRemoval"
  | "ensureReconciliationMarkersTable"
  | "getReconciliationMarker"
  | "setReconciliationMarker"
>;

export type GraphLifecycleBackend = Pick<
  GraphBackend,
  "clearGraph" | "bootstrapTables"
>;

export type QueryExecutionBackend = Pick<GraphBackend, "execute">;

export type RawQueryExecutionBackend = Pick<
  GraphBackend,
  "executeRaw" | "compileSql"
>;

export type SqlCompilationBackend = Pick<GraphBackend, "compileSql">;

export type RawStatementExecutionBackend = Pick<
  GraphBackend,
  "executeStatement" | "executeTemporaryStatement" | "executeDdl"
>;

export type BackendMaintenance = Pick<GraphBackend, "refreshStatistics">;

export type BackendTransactions = Pick<GraphBackend, "transaction">;

export type AdapterBackendTransactions<TNativeTransaction> = Pick<
  AdapterBackend<TNativeTransaction>,
  "transactionWithNative" | "adoptTransaction"
>;

export type BackendLifecycle = Pick<GraphBackend, "close">;

/**
 * Read-oriented transaction projection exposed by portable transaction
 * contexts. It preserves snapshot-aware graph reads and TypeGraph-compiled
 * row queries while excluding graph writes, arbitrary raw SQL, DDL,
 * transaction adoption, and lifecycle control.
 */
export type TransactionReadBackend = Readonly<
  BackendIdentity &
    GraphEntityReadBackend &
    SchemaReadBackend &
    QueryExecutionBackend &
    SqlCompilationBackend
>;

/**
 * Transaction backend — a backend scoped to a transaction.
 *
 * This is an explicit facet composition, not `Omit<GraphBackend, ...>`.
 * New top-level-only backend methods therefore do not silently appear on
 * transaction-scoped backends. `commitSchemaVersion`, `setActiveVersion`,
 * `refreshStatistics`, `transaction`, `adoptTransaction`, and `close` stay
 * deliberately absent: their guarantees depend on the top-level backend or
 * lifecycle owner rather than an already-open transaction.
 */
export type TransactionBackend = Readonly<
  BackendIdentity &
    GraphEntityReadBackend &
    GraphEntityWriteBackend &
    UniqueConstraintBackend &
    // The edge cardinality claim relation's surface. Inline rather than a named
    // member type: it is a different relation from `uniques` with a different
    // key, and naming it would add a type the public surface references but no
    // entrypoint exports.
    Pick<
      GraphBackend,
      | "claimEdgeCardinality"
      | "claimEdgeCardinalityGuarded"
      | "claimEdgeCardinalityBatch"
      | "purgeEdgeClaims"
    > &
    SchemaReadBackend &
    SchemaWriteFenceBackend &
    VectorOperationBackend &
    FulltextOperationBackend &
    IndexMaterializationBackend &
    CatalogBackend &
    LineageBackend &
    RecordedTimeBackend &
    ContributionMaterializationBackend &
    RemovalMaterializationBackend &
    GraphLifecycleBackend &
    QueryExecutionBackend &
    RawQueryExecutionBackend &
    RawStatementExecutionBackend
>;

/**
 * Transaction backend exposed only while the backend's schema-write lock is
 * held. Unlike the ordinary top-level `executeDdl` port, this DDL primitive is
 * explicitly transaction-scoped and must use the callback's transaction.
 *
 * @internal
 */
export type SchemaWriteTransactionBackend = TransactionBackend &
  Readonly<{
    executeStatement: NonNullable<TransactionBackend["executeStatement"]>;
    tableExists: (this: void, tableName: string) => Promise<boolean>;
    executeSchemaDdl: (this: void, ddl: string) => Promise<void>;
    deleteSchemaVectorSlotContribution: (
      this: void,
      slot: VectorSlot,
    ) => Promise<void>;
  }>;

/**
 * The target a schema-commit preflight runs against: a transaction backend
 * that MAY also carry the schema-write DDL primitive.
 *
 * Optional, deliberately. Both bundled backends pass the same
 * {@link SchemaWriteTransactionBackend} their `schemaWriteTransaction` exposes,
 * so DDL is available there; a custom backend may implement
 * {@link GraphBackend.commitSchemaVersionWithPreflight} over a transaction that
 * cannot run DDL. Declaring the port as present-or-absent lets a preflight that
 * needs it refuse with a typed error naming the capability, instead of calling
 * `undefined` — or, far worse, skipping the DDL and continuing.
 *
 * @internal
 */
export type SchemaCommitPreflightBackend = TransactionBackend &
  Readonly<{
    executeSchemaDdl?: (this: void, ddl: string) => Promise<void>;
  }>;

/**
 * Builds the actual runtime projection exposed as `TransactionContext.backend`.
 * This is an explicit object rather than a type-only narrowing so portable
 * callers cannot discover write or arbitrary-SQL methods on the underlying
 * transaction backend at runtime.
 *
 * @internal
 */
export function createTransactionReadBackend(
  backend: TransactionBackend,
): TransactionReadBackend {
  const getNodes = backend.getNodes;
  const getEdges = backend.getEdges;
  const compileSql = backend.compileSql;

  return Object.freeze({
    dialect: backend.dialect,
    capabilities: backend.capabilities,
    ...(backend.tableNames === undefined ?
      {}
    : { tableNames: backend.tableNames }),
    ...(backend.fulltextStrategy === undefined ?
      {}
    : { fulltextStrategy: backend.fulltextStrategy }),
    ...(backend.vectorStrategy === undefined ?
      {}
    : { vectorStrategy: backend.vectorStrategy }),
    getNode: (graphId, kind, id) => backend.getNode(graphId, kind, id),
    ...(getNodes === undefined ?
      {}
    : {
        getNodes: (graphId: string, kind: string, ids: readonly string[]) =>
          getNodes(graphId, kind, ids),
      }),
    getEdge: (graphId, id) => backend.getEdge(graphId, id),
    ...(getEdges === undefined ?
      {}
    : {
        getEdges: (graphId: string, ids: readonly string[]) =>
          getEdges(graphId, ids),
      }),
    countEdgesFrom: (params) => backend.countEdgesFrom(params),
    edgeExistsBetween: (params) => backend.edgeExistsBetween(params),
    findEdgesConnectedTo: (params) => backend.findEdgesConnectedTo(params),
    findNodesByKind: (params) => backend.findNodesByKind(params),
    countNodesByKind: (params) => backend.countNodesByKind(params),
    findEdgesByKind: (params) => backend.findEdgesByKind(params),
    countEdgesByKind: (params) => backend.countEdgesByKind(params),
    getActiveSchema: (graphId) => backend.getActiveSchema(graphId),
    getSchemaVersion: (graphId, version) =>
      backend.getSchemaVersion(graphId, version),
    execute: <T>(query: CompiledRowsSql) => backend.execute<T>(query),
    ...(compileSql === undefined ?
      {}
    : { compileSql: (query: SqlFragment) => compileSql(query) }),
  });
}

/**
 * Closes an owned resource after provisioning fails without replacing the
 * original domain error with a secondary cleanup failure.
 */
export async function closeAfterFailure(
  resource: Readonly<{ close: () => void | Promise<void> }>,
  provisioningError: unknown,
): Promise<never> {
  try {
    await resource.close();
  } catch {
    // The provisioning failure remains the actionable error. Managed close is
    // retryable when the resource is returned, but a failed factory has no
    // handle it can safely expose for a second attempt.
  }
  throw provisioningError;
}

/** Options for {@link runOptionallyInTransaction}. */
export type RunOptionallyInTransactionOptions = Readonly<{
  /**
   * Pass only when the toplevel backend method would recurse — pass the
   * operation-level backend so the no-transaction path doesn't loop back
   * through the same toplevel method.
   */
  fallback?: GraphBackend | TransactionBackend;
  /**
   * Options for the transaction this opens — most usefully
   * `accessMode: "read_only"`, which lets a multi-statement READ declare itself
   * to the engine instead of only promising it (SQLite issues `BEGIN` rather
   * than reserving the single writer slot with `BEGIN IMMEDIATE`; PostgreSQL
   * issues `BEGIN … READ ONLY`).
   *
   * Scopes the transaction and nothing else, so it is not honored on the
   * fallthrough path: a backend without interactive transactions opens no
   * transaction to configure. The callback receives an explicit execution
   * mode (`interactive-transaction` or `sequential`), so callers never infer
   * the execution boundary from backend object identity.
   */
  transaction?: TransactionOptions;
}>;

/** What {@link runOptionallyInTransaction} actually did for this invocation. */
export type OptionalTransactionExecution =
  | Readonly<{
      /** The callback runs on a transaction-scoped interactive session. */
      mode: "interactive-transaction";
    }>
  | Readonly<{
      /** The callback runs sequentially without an encompassing transaction. */
      mode: "sequential";
    }>;

/**
 * Runs `fn` inside a transaction when given a top-level backend that supports
 * one, falling through to a direct invocation otherwise. Lets call sites benefit
 * from atomicity on backends that have transactions while staying functional on
 * backends that don't (Cloudflare D1, `drizzle-orm/neon-http` over HTTP), and
 * avoids opening nested transactions when the caller already holds a
 * transaction-scoped backend. The single-statement race window is already
 * implicit on any backend without interactive transactions; callers that
 * cannot tolerate it must branch on the capability themselves.
 */
export async function runOptionallyInTransaction<T>(
  backend: GraphBackend | TransactionBackend,
  fn: (
    target: GraphBackend | TransactionBackend,
    execution: OptionalTransactionExecution,
  ) => Promise<T>,
  options?: RunOptionallyInTransactionOptions,
): Promise<T> {
  if (
    "transaction" in backend &&
    backend.capabilities.execution.interactiveTransactions
  ) {
    return backend.transaction(
      (tx) => fn(tx, { mode: "interactive-transaction" }),
      options?.transaction,
    );
  }
  return fn(options?.fallback ?? backend, { mode: "sequential" });
}

// ============================================================
// Additional Parameter Types
// ============================================================

/**
 * Parameters for inserting a unique constraint entry.
 */
export type InsertUniqueParams = Readonly<{
  graphId: string;
  nodeKind: string;
  constraintName: string;
  key: string;
  nodeId: string;
  concreteKind: string;
}>;

/**
 * Parameters for releasing a unique constraint entry.
 *
 * Every release is scoped to the claim's OWNER — the pair
 * `(concreteKind, nodeId)`, because a node id is unique only within its kind.
 * The two shapes differ only in whether the claim AXIS participates:
 *
 *  - **Lifecycle release** (`nodeKind` absent) — "give up every claim this node
 *    holds for this constraint and key, whatever axis it sits on". Soft delete,
 *    an update's key-change release and the resurrect diff use it, so a claim
 *    written under an older axis is still released by newer code.
 *  - **Compensating release** (`nodeKind` present) — "undo exactly the row I
 *    just claimed, at the axis I claimed it on". A failed write's rollback uses
 *    it, so it touches neither a row that predates the write nor one another
 *    node holds.
 */
export type DeleteUniqueParams = Readonly<{
  graphId: string;
  /**
   * The claim axis (`uniques.node_kind`) the release is restricted to. Absent
   * releases the owner's claims at every axis — see the two shapes above.
   */
  nodeKind?: string;
  constraintName: string;
  key: string;
  /** The claim owner's concrete kind (`uniques.concrete_kind`). */
  concreteKind: string;
  /** The claim owner's node id (`uniques.node_id`). */
  nodeId: string;
}>;

/** Parameters for permanently clearing uniqueness sidecars by node identity. */
export type HardDeleteUniquesByNodeIdsParams = Readonly<{
  graphId: string;
  concreteKind: string;
  nodeIds: readonly string[];
}>;

/** Parameters for permanently clearing every uniqueness claim a kind owns. */
export type HardDeleteUniquesByConcreteKindParams = Readonly<{
  graphId: string;
  concreteKind: string;
}>;

/**
 * One edge cardinality claim, named by the components its axis, its key and its
 * holder-liveness predicate are all built from.
 *
 * The components are passed RAW rather than pre-rendered: `EDGE_CARDINALITY_SPECS`
 * (`store/claims/edge-claims.ts`) is the one table that decides which endpoints
 * the key covers and what a holder must still be, and both the TypeScript probe
 * and the SQL builder read it. A caller that rendered the axis and key itself
 * would be a second spelling of that decision.
 */
export type ClaimEdgeCardinalityParams = Readonly<{
  graphId: string;
  /** The declared cardinality; `many` declares nothing and never claims. */
  cardinality: Exclude<Cardinality, "many">;
  edgeKind: string;
  /** The edge that will hold the axis if this claim lands. */
  edgeId: string;
  fromKind: string;
  fromId: string;
  toKind: string;
  toId: string;
}>;

/**
 * What a claim statement decided.
 *
 * `refused` carries the incumbent so the caller can say which edge holds the
 * axis; the typed refusal itself is the store's, built from the same
 * `checkCardinality` / `checkUniqueEdge` owners the probe uses, so a caller
 * cannot tell which layer refused.
 */
export type EdgeClaimOutcome =
  | Readonly<{ status: "claimed" }>
  | Readonly<{ status: "refused"; holderEdgeId: string }>;

/** Parameters for the housekeeping purge of claims held by named edges. */
export type PurgeEdgeClaimsParams = Readonly<{
  graphId: string;
  edgeIds: readonly string[];
}>;

/** One edge kind's declared cardinality, as the fence audit reads it. */
export type EdgeCardinalityDeclaration = Readonly<{
  edgeKind: string;
  /** `many` declares nothing, so it is unrepresentable here. */
  cardinality: Exclude<Cardinality, "many">;
}>;

/**
 * What a constraint-fence audit asks the database about: the declarations the
 * graph carries, one list per family.
 *
 * The declarations are passed rather than discovered because the backend holds
 * no schema — and because the `uniqueConstraintNames` restriction is
 * load-bearing rather than an optimization: the `uniques` relation also holds
 * disjointness claims, whose `node_kind` is a pair label rather than a kind and
 * for which no uniqueness axis can be computed. Those rows are covered by
 * `disjointKindPairs`, from the nodes relation.
 */
export type ReadConstraintFenceViolationsParams = Readonly<{
  graphId: string;
  /** Every unique constraint name the graph declares, in any scope. */
  uniqueConstraintNames: readonly string[];
  /** Every declared `disjointWith` pair, each read as one intersection. */
  disjointKindPairs: readonly (readonly [string, string])[];
  /** Every edge kind declaring a cardinality other than `many`. */
  edgeCardinalities: readonly EdgeCardinalityDeclaration[];
}>;

/**
 * One live `uniques` row that shares its `(constraint_name, key)` with at least
 * one other live row — a candidate, not a verdict: two rows contend only when
 * their `node_kind`s fold onto ONE claim axis, which the caller decides.
 */
export type ContendedUniqueRow = Readonly<{
  nodeKind: string;
  constraintName: string;
  key: string;
  concreteKind: string;
  nodeId: string;
}>;

/**
 * One live edge that shares its declared cardinality's population with at least
 * one other live edge. The endpoints are returned whole so the caller can name
 * the claim key through the one builder that renders it.
 */
export type ContendedEdgeRow = Readonly<{
  edgeKind: string;
  cardinality: Exclude<Cardinality, "many">;
  edgeId: string;
  fromKind: string;
  fromId: string;
  toKind: string;
  toId: string;
}>;

/** One node id live under BOTH kinds of a declared disjoint pair. */
export type DisjointOverlapRow = Readonly<{
  kinds: readonly [string, string];
  nodeId: string;
}>;

/** Everything one constraint-fence audit read, per family. */
export type ConstraintFenceViolationRows = Readonly<{
  contendedUniqueRows: readonly ContendedUniqueRow[];
  contendedEdgeRows: readonly ContendedEdgeRow[];
  disjointOverlaps: readonly DisjointOverlapRow[];
}>;

/**
 * Parameters for checking a unique constraint.
 */
export type CheckUniqueParams = Readonly<{
  graphId: string;
  nodeKind: string;
  constraintName: string;
  key: string;
  /** If true, also returns soft-deleted entries. Used by get-or-create operations. */
  includeDeleted?: boolean;
}>;

/**
 * Parameters for batch-checking unique constraints.
 */
export type CheckUniqueBatchParams = Readonly<{
  graphId: string;
  nodeKind: string;
  constraintName: string;
  keys: readonly string[];
  /** If true, also returns soft-deleted entries. Used by get-or-create operations. */
  includeDeleted?: boolean;
}>;

/**
 * Parameters for inserting a schema version.
 *
 * Used internally by backend implementations. Public callers go through
 * `commitSchemaVersion`, which handles the insert + activate atomically
 * with CAS guarantees.
 */
export type InsertSchemaParams = Readonly<{
  graphId: string;
  version: number;
  schemaHash: string;
  schemaDoc: SerializedSchema;
  isActive: boolean;
}>;

/**
 * The caller's claim about the currently-active schema version, used as
 * the optimistic compare-and-swap guard for `commitSchemaVersion`.
 *
 * - `{ kind: "initial" }` — caller is committing the first-ever version
 *   for this graph and asserts no active version exists yet.
 * - `{ kind: "active", version: N }` — caller observed version N as
 *   active and is committing version N+1 against that baseline. The
 *   commit fails with `StaleVersionError` if some other writer has
 *   advanced or rolled back the pointer in the meantime.
 *
 * Tagged-union form (rather than a magic `version: 0` sentinel) because
 * the two cases have materially different semantics: the initial path
 * skips the "deactivate prior" UPDATE, and the no-active-row state is
 * a *valid* expected state, not an out-of-band signal.
 */
export type CommitSchemaVersionExpected =
  Readonly<{ kind: "initial" }> | Readonly<{ kind: "active"; version: number }>;

/**
 * Parameters for `commitSchemaVersion`.
 */
export type CommitSchemaVersionParams = Readonly<{
  graphId: string;
  /** CAS guard — see `CommitSchemaVersionExpected`. */
  expected: CommitSchemaVersionExpected;
  /** The new version to insert and activate. */
  version: number;
  schemaHash: string;
  schemaDoc: SerializedSchema;
}>;

export type SchemaKindEmptinessProbe = Readonly<{
  entity: "node" | "edge";
  kind: string;
  /** Which rows make this schema transition unsafe. Must be chosen explicitly. */
  rows: "nonDeleted" | "all";
}>;

export type PopulatedSchemaKind = SchemaKindEmptinessProbe &
  Readonly<{ count: number }>;

export type CommitSchemaVersionIfKindsEmptyResult =
  | Readonly<{ status: "committed"; row: SchemaVersionRow }>
  | Readonly<{
      status: "populated";
      kinds: readonly PopulatedSchemaKind[];
    }>;

export type LockSchemaVersionForWriteParams = Readonly<{
  graphId: string;
  expectedVersion: number;
}>;

/** Parameters shared by the ordinary and fused schema-write fences. */
export type SchemaWriteFenceParams = LockSchemaVersionForWriteParams;

/**
 * Optional schema-managed write identity for a trusted import. Supplying it
 * acquires and validates the Store version's transaction-scoped schema fence;
 * omitting it leaves the import outside the versioned guarantee.
 */
export type TrustedImportOptions = Readonly<{
  schemaWrite?: Readonly<{ graphId: string; expectedVersion: number }>;
}>;

/**
 * Parameters for `setActiveVersion`.
 *
 * Flips the active pointer from `expected` to `version` for an existing
 * row. CAS prevents overwriting a concurrent rollback or commit.
 */
export type SetActiveVersionParams = Readonly<{
  graphId: string;
  /** CAS guard. Same semantics as `commitSchemaVersion.expected`. */
  expected: CommitSchemaVersionExpected;
  /** The version to mark active. Must already exist. */
  version: number;
}>;

/**
 * Parameters for counting edges from a source node.
 */
export type CountEdgesFromParams = Readonly<{
  graphId: string;
  edgeKind: string;
  fromKind: string;
  fromId: string;
  /** If true, only count edges where valid_to IS NULL */
  activeOnly?: boolean;
}>;

/**
 * Parameters for checking if an edge exists between two nodes.
 */
export type EdgeExistsBetweenParams = Readonly<{
  graphId: string;
  edgeKind: string;
  fromKind: string;
  fromId: string;
  toKind: string;
  toId: string;
}>;

/**
 * Parameters for finding edges connected to a node.
 */
export type FindEdgesConnectedToParams = Readonly<{
  graphId: string;
  nodeKind: string;
  nodeId: string;
}>;

/**
 * Parameters for finding nodes by kind.
 */
export type FindNodesByKindParams = Readonly<{
  graphId: string;
  kind: string;
  /** Max rows to return. */
  limit?: number;
  /** Offset. Present for backward compat; rebuild uses `after` instead. */
  offset?: number;
  /** If true, exclude deleted nodes. Default true. */
  excludeDeleted?: boolean;
  /** Temporal mode for filtering by validity period. */
  temporalMode?: TemporalMode;
  /** Timestamp for "current" and "asOf" temporal modes. */
  asOf?: string;
  /**
   * Stable ordering for keyset pagination. Default: "created_at" (existing
   * behavior). Rebuild should use "id" for iteration that is stable under
   * concurrent writes and shared timestamps.
   */
  orderBy?: "id" | "created_at";
  /**
   * Keyset cursor. Returns rows strictly greater (by `orderBy`) than this
   * value. When `orderBy: "id"`, compared lexicographically. Mutually
   * exclusive with `offset` — callers pick one.
   */
  after?: string;
}>;

/**
 * Parameters for counting nodes by kind.
 */
export type CountNodesByKindParams = Readonly<{
  graphId: string;
  kind: string;
  /** If true, exclude deleted nodes. Default true. */
  excludeDeleted?: boolean;
  /** Temporal mode for filtering by validity period. */
  temporalMode?: TemporalMode;
  /** Timestamp for "current" and "asOf" temporal modes. */
  asOf?: string;
}>;

/**
 * Parameters for finding edges by kind.
 */
export type FindEdgesByKindParams = Readonly<{
  graphId: string;
  kind: string;
  fromKind?: string;
  fromId?: string;
  toKind?: string;
  toId?: string;
  limit?: number;
  offset?: number;
  /** If true, exclude deleted edges. Default true. */
  excludeDeleted?: boolean;
  /** Temporal mode for filtering by validity period. */
  temporalMode?: TemporalMode;
  /** Timestamp for "current" and "asOf" temporal modes. */
  asOf?: string;
  /**
   * Stable ordering for keyset pagination. Default: "created_at" (existing
   * behavior). Use "id" for iteration that is stable under shared `created_at`
   * timestamps — the offset path orders by the NON-unique `created_at`, so a
   * full enumeration must page by the unique `id` to avoid skipping a row at a
   * page boundary. Mirrors {@link FindNodesByKindParams.orderBy}.
   */
  orderBy?: "id" | "created_at";
  /**
   * Keyset cursor. Returns rows strictly greater (by `orderBy`) than this value.
   * When `orderBy: "id"`, compared lexicographically. Mutually exclusive with
   * `offset` — callers pick one. Mirrors {@link FindNodesByKindParams.after}.
   */
  after?: string;
}>;

/**
 * The endpoint side a {@link FindEdgesByEndpointSetParams} read fans out over.
 */
export type EdgeEndpointSide = "from" | "to";

/**
 * Parameters for reading the edges of a SET of endpoints in one statement per
 * bind-budget chunk — the widened form of {@link FindEdgesByKindParams}'s
 * scalar `fromId` / `toId`.
 *
 * Deliberately a SEPARATE parameter type on a separate operation rather than
 * optional fields on `FindEdgesByKindParams`. A backend that did not implement
 * set membership would still type-check while ignoring the id list, and would
 * then return every edge of the kind — which the caller would rebucket into a
 * correct-looking answer at unbounded cost. Splitting the operation makes that
 * failure unreachable: support is detected by the method's presence, before any
 * read is issued.
 *
 * The shape also makes the previously-validated illegal states
 * unrepresentable. One `side` instead of two independent id lists means both
 * endpoints can never fan out at once; no scalar `fromId` / `toId` field means
 * a scalar and a set can never disagree; no `limit` / `offset` / `after` means
 * a global slice can never be requested across a read the backend splits into
 * bind-budget chunks.
 */
export type FindEdgesByEndpointSetParams = Readonly<{
  graphId: string;
  kind: string;
  /** Which endpoint column the id set constrains. */
  side: EdgeEndpointSide;
  /**
   * Kind of the fanned-out endpoint. Required: it is the index prefix that
   * makes the set a seek rather than a scan, so a set is always scoped to one
   * endpoint kind and a heterogeneous page costs one read per distinct kind.
   */
  endpointKind: string;
  /**
   * Endpoint NODE ids to match — deliberately not named `ids`, which in the
   * backend params vocabulary means edge ids (see
   * {@link DeleteEdgesBatchParams}); the write-surface assertion in
   * `recorded-capture/write-surface.ts` classifies params structurally and a
   * `{ graphId, ids }` read would be misread as a write.
   *
   * The backend deduplicates before splitting the list across bind-budget
   * chunks — a repeated id spanning two chunks would return its edges twice.
   * An empty list reads nothing and yields no rows.
   */
  endpointIds: readonly string[];
  /**
   * Maximum rows per distinct endpoint id, applied inside the statement (via
   * `ROW_NUMBER()` over the read's own ordering) rather than by the caller.
   * Only meaningful on a backend whose `capabilities.windowFunctions` is true;
   * callers must still cap client-side, so a backend that ignores this returns
   * a superset rather than a wrong answer. Unlike a global `limit`, a
   * per-endpoint cap composes with chunking: each endpoint's rows fall
   * entirely within one chunk.
   */
  limitPerEndpoint?: number;
  /** If true, exclude deleted edges. Default true. */
  excludeDeleted?: boolean;
  /** Temporal mode for filtering by validity period. */
  temporalMode?: TemporalMode;
  /** Timestamp for "current" and "asOf" temporal modes. */
  asOf?: string;
}>;

/**
 * Parameters for reading several edge kinds from several endpoint kinds.
 *
 * The endpoint pairs are joined as a relation, so round trips are independent
 * of the number of licensed edge-kind/endpoint-kind combinations. Large source
 * sets may still be split to respect the backend's bind-parameter budget.
 */
export type FindEdgesByHeterogeneousEndpointSetParams = Readonly<{
  graphId: string;
  side: EdgeEndpointSide;
  endpoints: readonly Readonly<{
    kind: string;
    id: string;
    /**
     * Optional endpoint on the opposite side of the edge. When present, the
     * set read performs an exact directed-pair seek instead of materializing
     * every edge incident to `kind` / `id` and leaving the caller to filter.
     * This is particularly important for hub nodes.
     */
    opposite?: Readonly<{ kind: string; id: string }>;
  }>[];
  edgeKinds: readonly string[];
  limitPerEndpoint?: number;
  /** If true, exclude deleted edges. Default true. */
  excludeDeleted?: boolean;
  /** Temporal mode for filtering by validity period. */
  temporalMode?: TemporalMode;
  /** Timestamp for "current" and "asOf" temporal modes. */
  asOf?: string;
}>;

/**
 * Parameters for counting edges by kind.
 */
export type CountEdgesByKindParams = Readonly<{
  graphId: string;
  kind: string;
  fromKind?: string;
  fromId?: string;
  toKind?: string;
  toId?: string;
  /** If true, exclude deleted edges. Default true. */
  excludeDeleted?: boolean;
  /** Temporal mode for filtering by validity period. */
  temporalMode?: TemporalMode;
  /** Timestamp for "current" and "asOf" temporal modes. */
  asOf?: string;
}>;

// ============================================================
// Default Capabilities
// ============================================================

/**
 * Conservative per-statement bound-parameter floor for SQLite. The
 * compiled-in `SQLITE_MAX_VARIABLE_NUMBER` defaulted to 999 before SQLite
 * 3.32.0; drivers whose real ceiling cannot be probed (async/remote
 * connections) keep this floor. Single source of truth for
 * {@link SQLITE_CAPABILITIES} and the SQLite backend's batch math fallback.
 */
export const SQLITE_MAX_BIND_PARAMETERS = 999;

/**
 * `SQLITE_MAX_VARIABLE_NUMBER` default since SQLite 3.32.0 (better-sqlite3
 * also compiles it in explicitly). Used when a synchronous driver's probe
 * confirms a modern build.
 */
export const MODERN_SQLITE_MAX_BIND_PARAMETERS = 32_766;

/**
 * Cloudflare D1's documented per-statement bound-parameter ceiling. Far
 * below the classic SQLite floor, so D1 detection must cap the budget or
 * batched writes fail at runtime.
 */
export const D1_MAX_BIND_PARAMETERS = 100;

/**
 * Cloudflare SQLite-backed Durable Objects' documented per-statement
 * bound-parameter ceiling. The resolved `do-sqlite` execution profile must
 * advertise this limit so every capability-driven batch path stays below it.
 */
export const DURABLE_OBJECT_MAX_BIND_PARAMETERS = 100;

/**
 * Safe bound-parameter ceiling shared by every bundled PostgreSQL driver.
 * The protocol count can represent 65535, but postgres.js rejects a statement
 * with 65534 bound values, so backend batch math uses the lower portable limit.
 */
export const POSTGRES_MAX_BIND_PARAMETERS = 65_533;

/**
 * Default capabilities for SQLite.
 */
export const SQLITE_CAPABILITIES: BackendCapabilities = Object.freeze({
  execution: Object.freeze({
    interactiveTransactions: true,
    atomicBatch: "none",
    unitOfWork: "interactive",
  }),
  windowFunctions: true, // SQLite has supported window functions since 3.25.0
  clearValidTo: true,
  returning: true, // SQLite has supported RETURNING since 3.35.0
  // The bundled schema ships both claim relations and the shared operation
  // backend implements every claim member.
  constraintClaims: true,
  durableEdgeMatchIdentity: true,
  maxBindParameters: SQLITE_MAX_BIND_PARAMETERS,
  // Generic SQLite builds do not guarantee ENABLE_MATH_FUNCTIONS. The local
  // better-sqlite3 factory overrides this flag for its bundled build contract.
  graphAnalytics: Object.freeze({ supported: true, mathFunctions: false }),
  recursiveTraversal: Object.freeze({ supported: true }),
  writeFence: Object.freeze({
    mechanism: "engine-serialized",
  }),
});

/**
 * Default capabilities for PostgreSQL.
 */
export const POSTGRES_CAPABILITIES: BackendCapabilities = Object.freeze({
  execution: Object.freeze({
    interactiveTransactions: true,
    atomicBatch: "none",
    unitOfWork: "interactive",
  }),
  windowFunctions: true, // PostgreSQL supports ROW_NUMBER() and related windows
  orderedAggregates: true,
  clearValidTo: true,
  returning: true, // PostgreSQL has supported RETURNING since 8.2
  // The bundled schema ships both claim relations and the shared operation
  // backend implements every claim member.
  constraintClaims: true,
  durableEdgeMatchIdentity: true,
  atomicNodeInsertClaims: true,
  maxBindParameters: POSTGRES_MAX_BIND_PARAMETERS,
  graphAnalytics: Object.freeze({ supported: true, mathFunctions: true }),
  recursiveTraversal: Object.freeze({ supported: true }),
  writeFence: Object.freeze({
    mechanism: "advisory",
    drain: "table-lock",
  }),
});
